import { WebGLDevice, type SceneImage } from './webgl';
import { guardWalkShot } from './guardWalk';
import { idleLibFrame } from './idleLib';
import { EMOTES, groundAt, structureAt, lerp, SKINS, type Entity, type World } from '@nine/shared';
import { settings } from '../settings';
import { minZoomPx } from './zoomFloor';
import { DuelFraming, dockSpace } from './duelFraming';
import { clothStreams, cullCrowd, screenDpr } from '../graphics';
import { Effects } from './effects';
import { eagleViews, type EaglePalette } from './mounts';
import {
  CARRIAGE_AX,
  CARRIAGE_AY,
  carriageFrame,
  carriagePose,
  carriageSignFrame,
  carriageView,
} from './carriage';
import { clingStep, density1x } from './density';
import { artH, artW, artX, artY, canvas, ctx2d, finishArt, touchCanvas } from './pixel';
import {
  FRAMES,
  type MotionId,
  MOTIONS,
  motionKeys,
  type Pose,
  SHEATHE_FRAMES,
  STANCE_MOTION_IDS,
  viewOf,
  visualScale,
  type SpriteBank,
  type SpriteSet,
} from './sprites';
import { drawForgeBurst, drawForgeFx, forgeShows, type FxLayer } from './forgeFx';
import { auraRim, aurasOf, drawAuras } from './auras';
import { emoteFrame } from './emotes';
import { SKILL_MOTION_IDS, SKILL_SWINGS, skillPose, skillSwingAt } from './skillFx';
import { bladesIn } from './rig/sets';
import { contactRadius, daylightAt, drawFigureShadows, lanternLift, lightingOn } from './lighting';
import { LookAt } from './lookAt';
import { MoveAnim, strideFps, type MoveCtx, type MoveShot } from './qinggongAnim';
import { duelMoveFrame } from './duelMoveAnim';
import { clientToWorld, worldToClient, type IsoCamera } from './camera';
import { CameraPitch, PITCH_MIN } from './cameraTurn';
import { CameraFollow } from './cameraFollow';
import { cutToBand, pitchBand, pitchDrop } from './pitchView';
import { FlightTilt } from './flightTilt';
import { TurnView } from './turnView';
import { PropGrid } from './propGrid';
import { StreamedProps, streamScenery } from './propStream';
import { buildProps, Ground, viewFor, type Prop } from './terrain';
import { TallGrass, type Wader } from './grass';
import { InteriorView } from './interior';
import { ellipse, heightOf, hidden, CROWD } from './drawKit';
import { globeFrame, globeUnbend } from './globe';
import { duelRing } from './passes/duelRing';
import { lockMark } from './passes/lockMarker';
import { bareHandedMove, longMoveFrame } from './longMove';
import { receiveFrame, type ReceiveFrame } from './rig/receivePlay';
import { hitReactDeath, hitReactFrame, hitReactGhost, hitReactOwns } from './hitReact';
import { drawFigureRim } from './figureRims';
import { hitStopShot } from './hitStop';
import { guardReadShot } from './guardRead';
import { PassRunner, type Outdoors, type PassFrame } from './passes/registry';
import { TurnedScenery, type TurnedStrip } from './turned/scenery';

// Every feature's drawing step registers itself from its own file (render/passes/registry.ts).
import.meta.glob(['./passes/*.ts', '!./passes/registry.ts'], { eager: true });
// C0-VFX: the lock forms' sword/qi effects (two passes) and the re-keyed motions' sub-frames (motions/wxPhase.ts).
import './ink/lockBind';
import { wxPhase as motionPhase } from './motions/wxPhase';
import { WX_CUT_STRIKE, wxCutPts } from './ink/game';

type Writable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * Zoom-step limits (see `Renderer.step`). The widest is the graphics tier's (`minZoomPx`, zoomFloor.ts; 1.0–1.4).
 */
const MAX_PX = 6;
/**
 * How much bigger an art pixel shows than a zoom step alone would make it: the whole world is on
 * one grid (the buildings'), drawn about 20% coarser.
 */
const PIXEL_SIZE = 1.25;
/**
 * Zoom speed: a constant rate in log scale (e-folds per second, ×3.5 a second), so a zoom never
 * crawls into place or lurches; a step (3→4) takes about a quarter second, the whole range under one.
 */
const ZOOM_RATE = 1.25;
const clampZoom = (s: number): number => Math.max(minZoomPx(), Math.min(MAX_PX, s));

/** One drawn frame: the image plus how it is placed. */
interface Shot {
  img: HTMLCanvasElement;
  dx: number;
  dy: number;
  /** Rotation in radians around a pivot `pivot` art pixels above the feet (negative = up). */
  rot: number;
  pivot: number;
  facing: number;
}

const silhouettes = new WeakMap<HTMLCanvasElement, Map<string, HTMLCanvasElement>>();
/** Eagle view and mirroring for each screen heading (0 = right, clockwise). */
const EAGLE_VIEW: readonly ['side' | 'front3' | 'front' | 'back3' | 'back', number][] = [
  ['side', 1],
  ['front3', 1],
  ['front', 1],
  ['front3', -1],
  ['side', -1],
  ['back3', -1],
  ['back', 1],
  ['back3', 1],
];

/** Blood Sabre disciples (and their master) carry a great sabre across the back. */
export const carriesGreatSabre = (e: { sprite: string }): boolean =>
  e.sprite.startsWith('heroBlood') || e.sprite === 'bloodMaster';

/** The frame's shape filled with one colour (cached), for hit flashes and charge glows. */
function silhouette(img: HTMLCanvasElement, color: string): HTMLCanvasElement {
  let byColor = silhouettes.get(img);
  if (!byColor) silhouettes.set(img, (byColor = new Map()));
  let c = byColor.get(color);
  if (!c) {
    c = canvas(img.width, img.height);
    const g = ctx2d(c);
    g.drawImage(img, 0, 0);
    g.globalCompositeOperation = 'source-in';
    g.fillStyle = color;
    g.fillRect(0, 0, c.width, c.height);
    byColor.set(color, c);
  }
  return c;
}

/**
 * Draws the world into a low-resolution canvas (true pixel art), upscales it with nearest-neighbour,
 * then draws crisp text (names, numbers, stance icons) on top at full resolution.
 */
/** A 2D context left on the GPU: for canvases drawn into every frame and never read back. */

/** A key pose's still frame in a sprite set (the stand-in while a motion frame is drawn). */
function keyPose(set: SpriteSet, pose: Pose): HTMLCanvasElement {
  const own = (set.poses as Partial<Record<Pose, HTMLCanvasElement>>)[pose];
  if (own) return own;
  if (pose === 'atk' || pose === 'block' || pose === 'cast' || pose === 'jump' || pose === 'sit')
    return set[pose];
  if (pose === 'ready') return set.ready?.[0] ?? set.idle[0]!;
  return set.idle[0]!;
}

/** The motion that gathers into each way of charging a skill. */
const CAST_MOTION = {
  cast: 'castCast',
  overhead: 'castOverhead',
  chest: 'castChest',
  lowGuard: 'castLowGuard',
  palm: 'castPalm',
  spear: 'castSpear',
  meditate: 'castMeditate',
  rage: 'castRage',
} as const satisfies Record<string, MotionId>;

/**
 * Where in a swing (0–1 of it) the blade meets the target: the frame a landed blow freezes on. Kept
 * early, in step with the swing trail the effects draw from the moment the blow lands.
 */
const SWING_CONTACT: Partial<Record<string, number>> = {
  // C0-VFX: the re-keyed cuts start from their wind-up key, so the blade meets the foe a little later.
  wxCleave: 0.34,
  wxRise: 0.34,
  cleave: 0.3,
  rising: 0.32,
  thrust: 0.26,
  palm: 0.26,
  overhead: 0.27,
  chest: 0.25,
};
/** The motion that takes each plain swing from its last key back into the fighting stance. */
const RECOVER_MOTION: Partial<Record<string, MotionId>> = {
  cleave: 'recoverFollow',
  thrust: 'recoverFollow',
  palm: 'recoverFollow',
  rising: 'recoverAtk',
  overhead: 'recoverCut',
};
/** Seconds to gather back into the stance after a swing, and to settle out of it or into it. */
const RECOVER_TIME = 0.16;
const SETTLE_TIME = 0.22;

/** Seconds the weapon takes to be put away or drawn (`SHEATHE_FRAMES` steps; see the 'sheathe' pose). */
const SHEATHE_TIME = 0.6;
/** Frames a second of standing and the ready stance: one slow breath in their `FRAMES` frames (as the old four at three). */
const IDLE_FPS = (FRAMES * 3) / 4;
/** Seconds between blinks, and how long the eyes stay shut. */
const BLINK_EVERY = 3.7;
const BLINK_TIME = 0.13;
/** Offered as the stand-in for a frame not drawn yet, to tell it apart (see `idleFrame`). */
let NOT_DRAWN: HTMLCanvasElement | undefined;
const notDrawn = () => (NOT_DRAWN ??= canvas(1, 1));

/**
 * A cutscene on the map (ui/cutscene.ts): where the camera looks, and the puppets drawn in place of
 * the entities they stand in for (same id) while it plays. Purely visual; the world is not touched.
 */
export interface Stage {
  /** Where the camera looks (tiles), or null to follow the player. */
  focus: { x: number; y: number } | null;
  puppets: ReadonlyMap<number, Entity>;
  /** The scene's own effects, drawn over the figures (world pixels, offset `ox`, `oy`). */
  draw?(g: CanvasRenderingContext2D, ox: number, oy: number, iso: IsoCamera, time: number): void;
}

export class Renderer {
  /**
   * Render step: the whole number (`minZoomPx()` to `MAX_PX`) the world picture is sized for, the
   * zoom rounded down (and no lower than `minZoomPx()`); the fraction above it is shown by stretching
   * the picture (`px`).
   */
  step = 3;
  /** Pixel scale on screen: one art pixel = PX CSS pixels (the zoom `scale` times `PIXEL_SIZE`). */
  px = 3 * PIXEL_SIZE;
  /** Zoom in step units, continuous from `minZoomPx()` to `MAX_PX`, moving toward `zoomGoal`. */
  private scale = 3;
  private zoomGoal = 3;
  /** The bout framing (duelFraming.ts), stepped each frame in `render`. */
  private readonly duelFraming = new DuelFraming();
  private duelFrame: { blend: number; foe: Entity | null } = { blend: 0, foe: null };
  /** Zoom held in (+1) or out (−1) by a key or the stick, or 0. */
  private zoomHeld = 0;
  private zoomMoving = false;
  /** Called when a zoom comes to rest (to save it). */
  onZoomSettle: (() => void) | null = null;
  private readonly low: HTMLCanvasElement;
  /** The camera's turn round the player (right-drag, keys, stick): render/turnView.ts. */
  readonly turn = new TurnView();
  /** C0-ROTATE: the camera's pitch (render/cameraTurn.ts), 30° (painted) down to 15°. */
  readonly pitch = new CameraPitch();
  private readonly flightTilt = new FlightTilt();
  private readonly follow = new CameraFollow();
  /** Who is following whom with their eyes (มองตาม; purely visual). */
  readonly lookAt = new LookAt();
  /**
   * Draw commands are batched by WebGL; the world stays in a GPU render texture through zoom.
   */
  private readonly lg: CanvasRenderingContext2D;
  private readonly mg: CanvasRenderingContext2D;
  /** Ground and scenery per camera angle, built on first use (the ground paints itself in chunks). */
  private readonly grounds: Ground[] = [];
  private readonly propSets: PropGrid[] = [];
  /** Inside a building: its floor, walls, furniture and light (interior.ts). */
  private readonly indoor = new InteriorView();
  private readonly grassSets: TallGrass[] = [];
  /** When the camera was last turning (ms). */
  private turnedAt = -1e9;
  /** Each quarter turn's own scenery (trees, rocks, buildings), built once: a fifth of a second. */
  private readonly mapProps: Prop[][] = [];
  /** The feature drawing steps (render/passes), each with its own state. */
  private readonly passes: PassRunner;
  /** C0-ROTATE: buildings and stone blocks drawn from their painted views at any angle (render/turned/). */
  private readonly turned = new TurnedScenery();
  private w = 0;
  private h = 0;
  private dpr = 1;
  /** Dressed figures drawn last frame, and so far this frame (see `CROWD`). */
  private crowd = 0;
  private dressedSeen = 0;
  /**
   * Canvas pixels per art pixel in the world picture: two, so the figures, drawn at the rig's double
   * resolution, keep the detail the studio shows; scenery drawn at one is enlarged onto the grid.
   */
  private readonly res = density1x() ? 1 : 2;
  private lowW = 0;
  private lowH = 0;
  private cam = { x: 0, y: 0 };
  /** Current world-pixel → low-canvas offset. */
  ox = 0;
  oy = 0;
  /** How far the cinematic camera has closed in (0 = not at all), eased per frame. */
  private closeIn = 0;
  /** This frame's zoom of the world picture: scale and the offset it is drawn at (CSS pixels). */
  private lens = { z: 1, x: 0, y: 0 };
  /** A cutscene's staging (ui/cutscene.ts), or null: its camera, its puppets and its effects. */
  stage: Stage | null = null;
  /** The player's own zoom goal (steps over `basePx`) while a cutscene holds the camera at its own scale. */
  private heldZoom: number | null = null;
  clickMark: { x: number; y: number; t: number } | null = null;
  /** A ground-targeted move being aimed: its slot, reach and blast radius (tiles). */
  aim: { slot: number; range: number; radius: number } | null = null;
  hoverClient: [number, number] | null = null;

  static async create(
    view: HTMLCanvasElement,
    world: World,
    sprites: SpriteBank,
    effects: Effects,
    iso: IsoCamera,
  ): Promise<Renderer> {
    const low = canvas(8, 8);
    const gpu = await WebGLDevice.create(view, low);
    return new Renderer(view, world, sprites, effects, iso, low, gpu);
  }

  private constructor(
    private readonly view: HTMLCanvasElement,
    private readonly world: World,
    private readonly sprites: SpriteBank,
    private readonly effects: Effects,
    readonly iso: IsoCamera,
    low: HTMLCanvasElement,
    private readonly gpu: WebGLDevice,
  ) {
    this.low = low;
    this.lg = gpu.world.context;
    this.mg = gpu.screen.context;
    [this.cam.x, this.cam.y] = iso.proj(world.player.x, world.player.y);
    this.passes = new PassRunner();
    this.resize();
    addEventListener('c0look-scenery', () => this.refreshScenery());
  }

  /** C0-LOOK: scenery is built again (a look setting changed); the painted ground is kept. */
  refreshScenery(): void {
    this.mapProps.length = 0;
    for (let k = 0; k < 4; k++) delete this.propSets[k];
  }

  resize(): void {
    this.w = innerWidth;
    this.h = innerHeight;
    // The graphics tier's sharpness (graphics.ts).
    this.dpr = screenDpr();
    // The zoom is kept as steps over the window's default scale, as the saved preference is.
    const base = this.basePx();
    const shift = this.base ? base - this.base : base - this.scale;
    this.base = base;
    this.scale = clampZoom(this.scale + shift);
    this.zoomGoal = clampZoom(this.zoomGoal + shift);
    this.fitZoom(true);
  }

  /** The window's default scale, last `resize`. */
  private base = 0;

  /**
   * Sizes the world picture for the zoom: drawn at the whole render step at or below it, so it
   * always covers the screen, and stretched by the fraction (`px`). The picture is rebuilt only
   * when that step changes; its sides are kept even so its centre stays on a whole pixel.
   */
  private fitZoom(force = false): void {
    const s = Math.abs(this.scale - Math.round(this.scale)) < 1e-6 ? Math.round(this.scale) : this.scale;
    this.scale = s;
    this.px = s * PIXEL_SIZE;
    const step = Math.max(minZoomPx(), Math.floor(s));
    if (step === this.step && !force) return;
    this.step = step;
    const rpx = step * PIXEL_SIZE;
    this.lowW = 2 * Math.ceil(this.w / rpx / 2);
    this.lowH = 2 * Math.ceil(this.h / rpx / 2);
    this.low.width = this.lowW * this.res;
    this.low.height = this.lowH * this.res;
    this.view.width = Math.round(this.w * this.dpr);
    this.view.height = Math.round(this.h * this.dpr);
    this.gpu.resize(this.view.width, this.view.height, this.low.width, this.low.height);
  }

  /** Default pixel scale for the window size; the figures are tall, so only big screens start at 3×. */
  private basePx(): number {
    return this.w < 1600 ? 2 : 3;
  }

  /**
   * Moves the zoom toward its goal (or on, while held) at `ZOOM_RATE`, by this frame's `dt`.
   * Calls `onZoomSettle` when it comes to rest.
   */
  private stepZoom(dt: number): void {
    const from = this.px;
    const d = ZOOM_RATE * dt;
    // A bout frames both fighters: the goal is capped so they fit above the dock (duelFraming.ts).
    const fr = this.duelFrame;
    let goal = this.zoomGoal;
    if (fr.foe && fr.blend > 0) {
      const a = this.iso.proj(this.world.player.x, this.world.player.y);
      const b = this.iso.proj(fr.foe.x, fr.foe.y);
      const fit = DuelFraming.fitScale(a, b, this.w, this.h, PIXEL_SIZE);
      goal = Math.max(minZoomPx(), Math.min(goal, goal + (fit - goal) * fr.blend));
    }
    if (this.zoomHeld) {
      this.scale = clampZoom(this.scale * Math.exp(this.zoomHeld * d));
      this.zoomGoal =
        this.zoomHeld > 0 ? Math.max(this.zoomGoal, this.scale) : Math.min(this.zoomGoal, this.scale);
    } else if (this.scale !== goal) {
      const gap = Math.log(goal / this.scale);
      this.scale = Math.abs(gap) <= d ? goal : this.scale * Math.exp(Math.sign(gap) * d);
    }
    // Applied here only, so the scale on screen changes with this frame's camera and lens.
    this.fitZoom();
    if (this.px !== from) this.zoomMoving = true;
    else if (this.zoomMoving) {
      this.zoomMoving = false;
      this.onZoomSettle?.();
    }
  }

  /** The player's zoom: steps over the window's default scale (fractional), as saved. */
  get zoom(): number {
    return this.heldZoom ?? this.zoomGoal - this.base;
  }

  /** A right-drag up or down (CSS pixels) tilts the camera (input.ts). */
  tilt(dy: number): void {
    this.pitch.drag(dy);
    this.flightTilt.drag();
  }

  /** Sets the pitch at once (degrees; screenshots). */
  setPitch(deg: number): void {
    this.pitch.set(deg);
    this.applyPitch(this.pitch.pitch);
  }

  private applyPitch(p: number): void {
    const k0 = this.iso.groundK;
    if (!this.iso.setPitch(p)) return;
    const cy = this.iso.centre()[1];
    this.cam.y = cy + ((this.cam.y - cy) * this.iso.groundK) / k0;
  }

  /** Turns the camera to an angle (quarter turns) at once, as when loading it (`exact`: see TurnView.jump). */
  setAngle(quarters: number, exact = false): void {
    this.turn.jump(quarters, this.iso, exact);
    [this.cam.x, this.cam.y] = this.iso.proj(this.world.player.x, this.world.player.y);
  }

  /** Sets the player's zoom (steps over the default scale) at once, as when loading it. */
  setZoom(zoom: number): void {
    this.scale = this.zoomGoal = clampZoom(this.base + zoom);
  }

  /**
   * Moves the zoom goal (`by`); not while a cutscene holds the camera. A gesture that turns back
   * starts from the zoom on screen, not from a goal still ahead of it the other way.
   */
  private aimZoom(by: (from: number) => number, now = false): void {
    if (this.heldZoom != null) return;
    const goal = this.zoomGoal;
    const ahead = goal - this.scale;
    const back = ahead !== 0 && Math.sign(by(goal) - goal) !== Math.sign(ahead);
    this.zoomGoal = clampZoom(by(back ? this.scale : goal));
    if (now) this.scale = this.zoomGoal;
  }

  /** Zooms in (+1) or out (−1) by one step, animated. */
  zoomBy(step: number): void {
    this.aimZoom((s) => s + step);
  }

  /**
   * Zooms by a scale factor, proportional to a gesture: animated, or at once (`now`) for a pinch
   * that follows the fingers.
   */
  zoomScale(factor: number, now = false): void {
    if (!Number.isFinite(factor) || factor <= 0) return;
    this.aimZoom((s) => s * factor, now);
  }

  /**
   * Holds the zoom in (+1) or out (−1) while a key or stick is held, or lets go (0). Pressing aims
   * a step ahead, so a tap still zooms a step; holding goes on steadily until let go.
   */
  zoomHold(dir: number): void {
    dir = Math.sign(dir);
    if (dir === this.zoomHeld) return;
    if (dir && this.heldZoom == null) this.zoomBy(dir);
    this.zoomHeld = this.heldZoom == null ? dir : 0;
  }

  /** Undoes this frame's camera zoom: client pixels → pixels of the unzoomed picture. */
  private unzoom(clientX: number, clientY: number): [number, number] {
    const v = this.lens;
    return [(clientX - v.x) / v.z, (clientY - v.y) / v.z];
  }

  screenToWorld(clientX: number, clientY: number): [number, number] {
    return this.iso.unproj(...clientToWorld(this.lens, this.px, this.ox, this.oy, clientX, clientY));
  }

  /**
   * A cutscene's pixel scale (data units, 4 = the screen's usual scale), or null to give the player
   * back their own zoom. Either way the zoom glides there at `ZOOM_RATE`.
   */
  stageZoom(px: number | null): void {
    if (px == null) {
      if (this.heldZoom == null) return;
      this.zoomGoal = clampZoom(this.base + this.heldZoom);
      this.heldZoom = null;
      return;
    }
    this.heldZoom ??= this.zoomGoal - this.base;
    this.zoomHeld = 0;
    this.zoomGoal = clampZoom(this.base + px - 4);
  }

  /** A ground point (tiles) on screen, in client pixels (this frame's camera and zoom). */
  worldToScreen(x: number, y: number): [number, number] {
    return worldToClient(this.lens, this.px, this.ox, this.oy, ...this.iso.proj(x, y));
  }

  /** The entities to draw: each as itself, or as the cutscene puppet standing in for it. */
  private *shown(): Generator<Entity> {
    const puppets = this.stage?.puppets;
    for (const e of this.world.entities.values()) {
      // In a duel the pet is out of the picture (nothing to hide a fighter behind).
      if (duelRing.on && e.kind === 'pet') continue;
      yield puppets?.get(e.id) ?? e;
    }
  }

  /** Whether the camera can zoom one more step in (+1) or out (−1). */
  canZoom(step: number): boolean {
    return step > 0 ? this.zoomGoal < MAX_PX : this.zoomGoal > minZoomPx();
  }

  /** Turns the camera to the next rest angle clockwise (+1) or counter-clockwise (−1), animated. */
  rotate(step: number): void {
    this.turn.control.nudge(step);
  }

  private terrain(): Outdoors {
    const r = this.iso.rot;
    // One painted ground at a time once the camera rests (its painted chunks are what takes the
    // memory); every one met while it turns, so a turn repaints nothing it has passed. The scenery
    // of an angle, once built (a fifth of a second), is kept for the next turn.
    const now = performance.now();
    if (this.turn.control.busy) this.turnedAt = now;
    // Kept a few seconds after it settles too, for a turn straight back.
    if (now - this.turnedAt > 8000)
      for (let k = 0; k < 4; k++)
        if (k !== r && this.grounds[k]) {
          this.grounds[k].close();
          delete this.grounds[k];
          delete this.propSets[k];
        }
    const m = this.iso.view();
    const view = viewFor(m.w, r);
    let ground = this.grounds[r];
    if (!ground) {
      ground = this.grounds[r] = new Ground(m, this.iso.origin, view, { n: m.w, rot: r });
      this.grassSets[r] ??= new TallGrass(m, this.iso.origin, view);
    }
    if (!this.propSets[r]) {
      // The passes' own scenery (the training spots', kept by their ground) joins the map's, after it.
      const extra = this.passes.scenery({ map: m, origin: this.iso.origin, view, ground });
      // A big map's scenery is made per chunk near the camera (propStream.ts, lane C0-P1).
      if (streamScenery(m)) this.propSets[r] = new StreamedProps(m, this.iso.origin, view, extra);
      else this.propSets[r] = new PropGrid([...(this.mapProps[r] ??= buildProps(m, this.iso.origin, view)), ...extra]);
    }
    return {
      ground,
      props: this.propSets[r]!,
      grass: this.grassSets[r]!,
    };
  }

  /** Front-most pickable entity under the cursor. */
  pick(clientX: number, clientY: number): Entity | null {
    const [cx, cy] = this.unzoom(clientX, clientY);
    const [lx, ly] = globeUnbend(cx / this.px, cy / this.px);
    let best: Entity | null = null;
    for (const e of this.world.entities.values()) {
      const folk = e.kind === 'ambient' && !!e.folk;
      if (e.dead || hidden(e) || (e.kind !== 'monster' && e.kind !== 'npc' && e.kind !== 'rival' && !folk))
        continue;
      if (e.sprite === 'carriage') {
        // The coach (render/carriage.ts): its cabin, round its middle.
        const [sx, sy] = this.iso.proj(e.x, e.y);
        const [x, y] = [sx + this.ox, sy + this.oy];
        const on = lx > x - 34 && lx < x + 34 && ly > y - 74 && ly < y + 8;
        if (on && (!best || this.iso.depth(e.x, e.y) > this.iso.depth(best.x, best.y))) best = e;
        continue;
      }
      const set = this.sprites.of(e);
      if (!set) continue;
      const [sx, sy] = this.iso.proj(e.x, e.y);
      const x = sx + this.ox;
      const y = sy + this.oy;
      const s = visualScale(e, set);
      if (
        lx > x - 10 * s &&
        lx < x + 10 * s &&
        ly > y - set.h * s &&
        ly < y + 3 &&
        (!best || this.iso.depth(e.x, e.y) > this.iso.depth(best.x, best.y))
      )
        best = e;
    }
    return best;
  }

  /** Picks the frame for an entity and how to move it: lunge offset, rotation, facing flips. */
  private frameOf(e: Entity, set: SpriteSet, facing: number): Shot {
    const A = this.effects.anim(e.id);
    const t = this.world.time;
    const P = set.poses;
    const shot = (img: HTMLCanvasElement | undefined, o: Partial<Shot> = {}): Shot => ({
      img: img ?? set.idle[0]!,
      dx: 0,
      dy: 0,
      rot: 0,
      pivot: 0,
      facing,
      ...o,
    });
    // Screen offset `m` pixels along the heading (negative = knocked back).
    const ha = (this.iso.heading(e) * Math.PI) / 4;
    const push = (m: number) => ({
      dx: Math.round(Math.cos(ha) * m),
      dy: Math.round(Math.sin(ha) * m * 0.5),
    });
    /**
     * Frame `u` (0–1) of a motion between key poses, for figures drawn with the rig (else none).
     * Until it is drawn, the key pose nearest it stands in (the still the game always showed).
     */
    const motion = (id: MotionId, u: number) => {
      if (!set.extra) return undefined;
      const ph = motionPhase(id, Math.max(0, Math.min(1, u)) * (FRAMES - 1));
      const { a, b, t: k } = motionKeys(ph);
      return set.extra('motion', ph, false, () => keyPose(set, k < 0.5 ? a : b));
    };
    if (e === this.world.player || e.combatT > 0) this.warm(set);
    if (e === this.world.player) this.warmSkills(set);
    /**
     * Where in a motion's keys (0 = the first key) it is at `k` of the action, from `[k, key]` points:
     * held on a key between two points at the same key, moving between keys otherwise.
     */
    const along = (k: number, pts: readonly (readonly [number, number])[]) => {
      for (let i = 1; i < pts.length; i++) {
        const [k0, u0] = pts[i - 1]!;
        const [k1, u1] = pts[i]!;
        if (k <= k1) return u0 + (u1 - u0) * Math.max(0, Math.min(1, (k - k0) / Math.max(1e-6, k1 - k0)));
      }
      return pts[pts.length - 1]![1];
    };
    const cycle = (frames: HTMLCanvasElement[], fps: number) =>
      frames[Math.abs(Math.floor(e.animT * fps)) % frames.length]!;

    // Birds on the wing.
    if (set.fly && (e.z > 0.5 || e.critter?.state === 'fly'))
      return shot(cycle(set.fly, (14 * set.fly.length) / 4));
    /** A receive motion (render/rig/receive.ts) on the rig, whirled, lifted or laid down beside it as its keys say. */
    const received = (rf: Omit<ReceiveFrame, 'id'> & { id: MotionId }) => {
      const v = rf.turn ? viewOf(this.sprites.of(e) ?? set, this.iso.heading(e) + rf.turn, facing) : { set, facing };
      const ph = motionPhase(rf.id, rf.u * (FRAMES - 1));
      const { a, b, t: kk } = motionKeys(ph);
      const img = v.set.extra
        ? v.set.extra('motion', ph, false, () => keyPose(v.set, kk < 0.5 ? a : b))
        : (P.hurt ?? set.idle[0]);
      return shot(img, { facing: v.facing, dy: -Math.round(rf.lift), rot: rf.rot, pivot: rf.rot ? -3 : 0 });
    };
    // On the rig, death is the receive fall (render/hitReact.ts); other figures keep the toppled hurt still.
    if (e.dead && set.extra) return received(hitReactDeath(e, t - A.deathAt));
    if (e.dead) {
      // Rocked back on the feet a moment by the last blow, then toppling backwards, faster as it
      // goes, onto the ground, where the body jolts up a little once before it lies still.
      const d = t - A.deathAt;
      const k = Math.max(0, Math.min(1, (d - 0.08) / 0.32));
      const jolt = d > 0.4 && d < 0.56 ? Math.sin(((d - 0.4) / 0.16) * Math.PI) * 0.09 : 0;
      return shot(P.hurt ?? set.idle[0], {
        rot: -(Math.PI / 2) * k * k + jolt,
        pivot: 0,
        ...(d < 0.08 ? push(-1) : {}),
      });
    }
    // Movement and วิชาตัวเบา (render/qinggongAnim.ts): riding, the air, the dash, landing, water.
    const mv: MoveCtx = {
      e,
      set,
      t,
      facing,
      motion,
      pose: (p, ph = 0) => set.extra?.(p, ph),
      flipAt: A.flipAt,
      landAt: A.landAt,
      qg: e === this.world.player ? this.world.qinggong : null,
      ready: e.combatT > 0 && !!set.ready,
    };
    this.moveAnim.observe(mv);
    const moved = (m: MoveShot | null) => (m ? shot(m.img, m) : null);
    // Riding the flying sword: balanced on the blade, rocking with it; other mounts carry the rider still.
    if (e.mount && e.swingT <= 0) return moved(this.moveAnim.ride(mv)) ?? shot(cycle(set.idle, IDLE_FPS));
    // Qinggong in the bout (render/duelMoveAnim.ts): each step, dive and slide its own motion, facing the foe.
    // Long moves (ท่ายาว, render/longMove.ts): the ult's tell, the set piece's beats (caster and held foe), the whiff.
    const lm = set.extra ? longMoveFrame(this.world, e, (v) => this.iso.heading(v)) : null;
    if (lm) return received(lm);
    const dm = set.extra ? duelMoveFrame(this.world, e, (v) => this.iso.heading(v)) : null;
    if (dm) return received(dm);
    // In the air: the jump's arc and the arts, unless striking or dashing (those play their own frames).
    if (e.z > 0.5 && e.swingT <= 0 && e.dashT <= 0) {
      const air = moved(this.moveAnim.air(mv));
      if (air) return air;
    }
    const after =
      moved(this.moveAnim.dash(mv)) ?? moved(this.moveAnim.settle(mv)) ?? moved(this.moveAnim.water(mv));
    if (after) return after;
    if (e.meditating) return shot(set.sit);
    // Putting the weapon away or drawing it: standing, or in stride while walking.
    const sheathing = t - A.sheatheAt;
    if (sheathing < SHEATHE_TIME && set.extra && e.moving && e.swingT <= 0 && !e.cast) {
      const stow = moved(this.moveAnim.stowWalking(mv, sheathing, SHEATHE_TIME, A.sheatheOn));
      if (stow) return stow;
    }
    if (sheathing < SHEATHE_TIME && set.extra && !e.moving && e.swingT <= 0 && !e.cast)
      return shot(
        set.extra(
          A.sheatheOn ? 'sheathe' : 'unsheathe',
          Math.min(SHEATHE_FRAMES - 1, Math.floor((sheathing / SHEATHE_TIME) * SHEATHE_FRAMES)),
        ),
      );
    // Held in a locked form (ท่าล็อกเป้า): the target plays the beat's receive motion (render/rig/receive.ts), whirled,
    // lifted or laid down beside it as the motion keys say; figures without the rig keep the hurt pose.
    const fp = lockMark.pose.get(e.id);
    if (fp && fp.role === 'target') return received(receiveFrame(e.id, fp.kind, fp.k));
    // An ordinary blow on the rig: the receive motion its weight picks (render/hitReact.ts); it gives way to own actions.
    const gr = guardReadShot(this.world, e, set, this.sprites.of(e), this.iso, facing); // render/guardRead.ts
    if (gr) return shot(gr.img, gr);
    const hr = set.extra ? hitReactFrame(e, t) : null;
    if (hr) return received(hr);
    if (e.stunT > 0) return shot(P.hurt ?? set.idle[0], { dx: Math.round(Math.sin(e.animT * 30)) });
    if (e.hitT > 0 && !e.blocking && !(set.extra && hitReactOwns(e.id, t)))
      // Rocked back by the blow, then gathering again toward the ready stance.
      return shot(
        motion(
          'hurt',
          along(1 - e.hitT / 0.12, [
            [0, 0],
            [0.45, 0],
            [1, 1],
          ]),
        ) ??
          P.hurt ??
          set.idle[0],
        push(-(e.hitT / 0.12) * 2),
      );
    if (e.cast) {
      const k = e.cast.t / e.cast.windup;
      // Trembling with gathered power just before release.
      const dx = k > 0.6 ? (Math.floor(t * 40) % 2 ? 1 : -1) * (k > 0.85 ? 1 : 0.5) : 0;
      // Gathering into the casting pose, then holding it while the power builds.
      const id = CAST_MOTION[A.castStyle];
      const pose =
        A.castStyle === 'cast' ? set.cast : (P as Partial<Record<string, HTMLCanvasElement>>)[A.castStyle];
      return shot(motion(id, k / 0.35) ?? pose ?? set.cast, { dx: Math.round(dx) });
    }
    if (e.blocking) {
      // Raising the guard, holding it; a blow taken on it jars it back, then it settles again.
      if (A.blockSince < 0) A.blockSince = t;
      const since = t - A.guardAt;
      if (since < 0.18)
        return shot(
          motion('guardHit', since / 0.18) ?? P.blockHit ?? set.block,
          push(since < 0.08 ? -2 : -1),
        );
      // Guarding on the move: the guarded shuffle, facing the foe (render/guardWalk.ts).
      const foe = e.targetId !== null ? this.world.entities.get(e.targetId) : null;
      const gw = e.moving ? guardWalkShot(e, set, facing, this.sprites.of(e), this.iso, foe) : null;
      if (gw) return shot(gw.img, { facing: gw.facing });
      return shot(motion('guardUp', (t - A.blockSince) / 0.12) ?? set.block);
    }
    A.blockSince = -1;
    if (e.spinT > 0) {
      // Whirlwind: the body whips round through all eight directions, blade out, several turns a
      // second, with a slight lean into the turn.
      const v = viewOf(this.sprites.of(e)!, this.iso.heading(e) + Math.floor(t * 26), facing);
      return shot(v.set.poses.cut ?? v.set.atk, { facing: v.facing, dy: Math.round(Math.sin(t * 13)) });
    }
    // Skill motions (skillFx.ts `SKILL_SWINGS`): a spear thrust, a fan's spin, a palm push, a leaping
    // strike, sword qi, a healing cast, an ultimate. The wind-up key is held so the move reads, then
    // the motion plays through, frozen on the blow for the hit-stop; standing still after the
    // simulation's swing, it plays on for the least time its style reads in.
    const skw = SKILL_SWINGS[A.style];
    if (skw && (e.swingT > 0 || (t - A.swingAt < A.swingDur && !e.moving && e.dashT <= 0))) {
      const at = skillSwingAt(skw, t - A.swingAt, A.swingDur, A.hitstopAt - A.swingAt);
      const p = skillPose(skw, at.k, A.charged, at.stopped);
      // Then back into the stance from where the motion ended.
      if (skw.recover) this.recovering.set(e.id, { id: skw.recover, end: A.swingAt + A.swingDur });
      else this.recovering.delete(e.id);
      const big = t - A.auraAt < A.swingDur + 0.05 ? 1 + A.auraPow * 0.5 : 1;
      const m = push(p.reach * big);
      if (!P.cut || !set.extra) return shot(set.atk, { ...m, dy: m.dy - Math.round(p.hop) });
      // A spin whirls the body round through the views as it turns.
      const v =
        p.turn >= 0
          ? viewOf(this.sprites.of(e)!, this.iso.heading(e) + Math.floor(p.turn * 8), facing)
          : null;
      const img = v?.set.extra
        ? v.set.extra(
            'motion',
            motionPhase(skw.id, p.u * (FRAMES - 1)),
            false,
            () => v.set.poses.cut ?? v.set.atk,
          )
        : motion(skw.id, p.u);
      return shot(img, { ...m, dy: m.dy - Math.round(p.hop), ...(v ? { facing: v.facing } : {}) });
    }
    if (e.swingT > 0) {
      const dur = A.swingDur;
      const since = t - A.swingAt;
      const k0 = since >= 0 && since <= dur ? since / dur : 1 - Math.min(1, e.swingT / dur);
      // Moves step in harder than plain blows (the qi aura marks a move just used).
      const big = t - A.auraAt < dur + 0.05 ? 1 + A.auraPow : 1;
      // Anticipation: the wind-up key held a moment (a move gathered in a cast has had its own;
      // the overhead's raised blade always shows). The blade then meets the target at `kc` of the
      // swing, in step with the swing arc drawn by the effects (its head ends about 0.07 s in).
      const kc = SWING_CONTACT[A.style] ?? 0.3;
      const pre = A.style === 'overhead' ? 0.12 : A.charged ? 0 : 0.14;
      // Hit-stop: a blow that lands with the swing (a plain attack lands on the tick it starts)
      // freezes on the contact frame for `hold`, then the follow-through plays out in the time
      // left, so the swing still ends when the world says. A hit landing later (a delayed move)
      // freezes on the strike when it lands.
      const hold = A.hitstopHold ?? (A.style === 'overhead' ? 0.09 : 0.06);
      const start = t - k0 * dur;
      const tc = start + kc * dur;
      const early = A.hitstopAt >= start - 0.03 && A.hitstopAt <= tc;
      let k = k0;
      let stopped = !early && t - A.hitstopAt < hold;
      if (early && t >= tc) {
        if (t < tc + hold) {
          k = kc;
          stopped = true;
        } else k = kc + (1 - kc) * Math.min(1, (t - tc - hold) / Math.max(1e-3, start + dur - tc - hold));
      }
      // The body rocks back a pixel in the wind-up, drives in to the contact, and eases back by
      // the end of the swing (thrusts and palms step in further).
      const reach = 3 * big * (A.style === 'thrust' || A.style === 'palm' ? 1.5 : 1);
      const smooth = (u: number) => {
        const v = Math.max(0, Math.min(1, u));
        return v * v * (3 - 2 * v);
      };
      const back = pre > 0 ? -1 : 0;
      const lunge =
        k < pre
          ? back
          : k < kc
            ? back + (reach - back) * smooth((k - pre) / Math.max(1e-3, kc - pre))
            : reach * (1 - smooth((k - kc) / (1 - kc)));
      if (!P.cut) return shot(set.atk, push(lunge));
      // After the swing the figure gathers back into its stance from where the swing ended.
      const rec = RECOVER_MOTION[A.style];
      if (rec) this.recovering.set(e.id, { id: rec, end: Math.min(start + dur, t + e.swingT) });
      else this.recovering.delete(e.id);
      // Anticipation, the blade passing mid-swing, the end of the strike, follow-through (stills,
      // for figures without the rig).
      const phase = stopped ? 2 : k < pre ? 0 : k < kc ? 1 : k < 0.62 ? 2 : 3;
      const pick = (frames: (HTMLCanvasElement | undefined)[]) => frames[phase] ?? set.atk;
      // Drawn with the rig: the swing as one motion — the anticipation held, the blade sweeping
      // through, the follow-through — frozen on the strike for the hit-stop when it lands.
      const swing = (
        id: MotionId,
        pts: readonly (readonly [number, number])[],
        strike: number,
        keys: number,
      ) => motion(id, (stopped ? strike : along(k, pts)) / (keys - 1));
      // Each swing as `[k, key]` points: the wind-up held to `pre`, the blade accelerating through
      // to the contact at `kc` (the strike key the hit-stop freezes on), then carried through.
      const sweep =
        A.style === 'wxCleave' || A.style === 'wxRise'
          ? // C0-VFX: the re-keyed cuts, from the wind-up key through the overshoot to the stance.
            swing(A.style, wxCutPts(A.style, pre, kc), WX_CUT_STRIKE[A.style], A.style === 'wxCleave' ? 10 : 9)
          : A.style === 'cleave'
          ? // Raised behind (windup), brought over (atk), cut down through the target (cut), the
            // blade carried on low past it (follow).
            swing(
              'cleave',
              [
                [0, 0],
                [pre, 0],
                [pre + (kc - pre) * 0.5, 1],
                [kc, 2],
                [0.7, 3],
                [1, 3],
              ],
              2,
              4,
            )
          : A.style === 'rising'
            ? // From the low guard the blade sweeps up: it meets the target rising, past level,
              // and finishes high.
              swing(
                'rising',
                [
                  [0, 0],
                  [pre, 0],
                  [pre + (kc - pre) * 0.6, 1],
                  [kc, 1.5],
                  [0.7, 2],
                  [1, 2],
                ],
                1.5,
                3,
              )
            : A.style === 'thrust' || A.style === 'palm'
              ? // Driven straight out, held at full extension a beat, then drawn back.
                swing(
                  'thrust',
                  [
                    [0, 0],
                    [pre, 0],
                    [kc, 1],
                    [kc + 0.22, 1],
                    [0.9, 2],
                    [1, 2],
                  ],
                  1,
                  3,
                )
              : A.style === 'overhead'
                ? // The blade raised high, then brought straight down; the cut is held as the weight
                  // settles (the finisher's pause).
                  swing(
                    'overhead',
                    [
                      [0, 0],
                      [pre, 0],
                      [kc, 1],
                      [1, 1],
                    ],
                    1,
                    2,
                  )
                : A.style === 'chest'
                  ? swing(
                      'chest',
                      [
                        [0, 0],
                        [kc, 1],
                        [1, 1],
                      ],
                      1,
                      2,
                    )
                  : undefined;
      if (sweep) return shot(sweep, push(lunge));
      switch (A.style) {
        case 'cleave':
          return shot(pick([P.windup, set.atk, P.cut, P.follow]), push(lunge));
        case 'rising':
          return shot(pick([P.lowGuard, P.thrust, set.atk, set.atk]), push(lunge));
        case 'thrust':
        case 'palm':
          return shot(pick([P.lowGuard, P.thrust, P.thrust, P.follow]), push(lunge));
        case 'overhead':
          return shot(k < kc ? P.overhead : P.cut, push(lunge));
        case 'chest':
          return shot(P.chest);
        case 'spin': {
          // Whirl: the body turns a full circle through all eight directions.
          const v = viewOf(this.sprites.of(e)!, this.iso.heading(e) + Math.floor(k * 16), facing);
          return shot(v.set.poses.cut ?? v.set.atk, { facing: v.facing });
        }
      }
    }
    // ท่าโพส: the emote's pose for this moment.
    const pose = e.emote && set.extra ? emoteFrame(e.emote, e.emoteT) : null;
    if (pose) return shot(set.extra!(pose[0], pose[1]));
    const life = this.lifeFrame(e, set);
    if (life) return shot(life);
    if (e.moving) {
      // Figures stride at an unhurried pace that quickens only gently with speed (a little under one full
      // stride, two long steps, per second at the player's speed); the movement is far quicker than
      // feet could keep up with, so matching it step for step would turn the run into a shuffle.
      // Beasts keep a gait tied to their speed.
      // People (they have strike poses) and beasts pace differently; both take `FRAMES` steps a stride.
      const fps = strideFps(e, set);
      if (e.sprinting && set.sprint) return shot(cycle(set.sprint, fps * 1.35));
      return shot(cycle(set.walk, fps));
    }
    // Just stopped walking: easing down from the stride into standing at ease.
    const stopping = moved(this.moveAnim.stop(mv));
    if (stopping) return stopping;
    // มองตาม: standing (or ready) with the eyes and head on someone (render/lookAt.ts).
    const gazing = this.lookAt.frame(e, set, IDLE_FPS, this.world.time);
    if (gazing) return shot(gazing);
    // Between blows a fighter holds a ready stance instead of standing idle.
    const ready = e.combatT > 0 && !!set.ready && !e.dead;
    if (set.extra && set.ready) {
      // Just after a swing: gathering back from where it ended into the stance.
      const rec = this.recovering.get(e.id);
      if (rec) {
        const u = (t - rec.end) / RECOVER_TIME;
        if (u < 1) {
          this.stances.set(e.id, { ready: true, at: -9, seen: t });
          const img = motion(rec.id, Math.max(0, u));
          if (img) return shot(img);
        } else this.recovering.delete(e.id);
      }
      // Dropping out of the stance once the fight is over (or taking it up): eased between the two
      // instead of a jump, when the figure was standing the frame before.
      const st = this.stances.get(e.id);
      const next =
        !st || t - st.seen > 0.1
          ? { ready, at: -9, seen: t }
          : st.ready !== ready
            ? { ready, at: t, seen: t }
            : { ...st, seen: t };
      this.stances.set(e.id, next);
      const u = (t - next.at) / SETTLE_TIME;
      if (u < 1) {
        const img = motion('settle', ready ? 1 - u : u);
        if (img) return shot(img);
      }
    }
    if (ready) return shot(cycle(set.ready!, IDLE_FPS));
    // The standing-idle library (render/idleLib.ts), when switched on.
    return shot(idleLibFrame(e, set, this.world) ?? this.idleFrame(e, set) ?? cycle(set.idle, IDLE_FPS));
  }

  /** Sets whose fighting motions have been asked for ahead of need. */
  private readonly warmed = new WeakSet<SpriteSet>();
  /** By entity: the motion back into the stance after its last swing, and when that swing ended. */
  private readonly recovering = new Map<number, { id: MotionId; end: number }>();
  /** Jumps, the qinggong arts, landing, the dash and stopping (render/qinggongAnim.ts). */
  private readonly moveAnim = new MoveAnim();
  /**
   * By entity: whether it last stood in the fighting stance (else at ease), since when, and when it
   * was last seen standing, to ease between the two instead of jumping.
   */
  private readonly stances = new Map<number, { ready: boolean; at: number; seen: number }>();

  /**
   * Asks for a fighter's strike, cast, guard and hit motions to be drawn (in idle time or off the
   * main thread) before they are needed: a blow lasts a third of a second, too short to wait for.
   */
  private warm(set: SpriteSet): void {
    if (!set.extra || this.warmed.has(set)) return;
    this.warmed.add(set);
    for (const m of MOTIONS)
      // (The idle library, motions/idles.ts, is drawn only for the idle a figure stands in.)
      if (m.id !== 'kata' && !SKILL_MOTION_IDS.has(m.id) && !STANCE_MOTION_IDS.has(m.id) && !m.id.startsWith('idl:'))
        for (let f = 0; f < FRAMES; f++) set.extra('motion', motionPhase(m.id, f), false, () => set.idle[0]!);
  }

  /** Sets whose skill motions have been asked for ahead of need (the player's only). */
  private readonly warmedSkills = new WeakSet<SpriteSet>();

  private warmSkills(set: SpriteSet): void {
    if (!set.extra || this.warmedSkills.has(set)) return;
    this.warmedSkills.add(set);
    for (const id of SKILL_MOTION_IDS)
      for (let f = 0; f < FRAMES; f++) set.extra('motion', motionPhase(id, f), false, () => set.idle[0]!);
  }

  /** Townsfolk at work or reacting (a bow, a wave, cowering), and the hauling walk. */
  private lifeFrame(e: Entity, set: SpriteSet): HTMLCanvasElement | null {
    const f = e.folk;
    const x = set.extra;
    if (!f || !x) return null;
    const t = this.world.time + (e.id % 17) * 0.37;
    if (e.moving) {
      if (f.walking && f.act === 'haul') {
        const fps = Math.max(5, Math.min(12, (3.5 * Math.sqrt(e.speed) * 12) / 12));
        return x('haul', Math.abs(Math.floor(e.animT * fps)) % 12);
      }
      return null;
    }
    if (f.gesture === 'bow') return x('bow');
    if (f.gesture === 'wave') return x('wave', Math.floor(t * 5) % 2);
    if (f.gesture === 'cower') return x('cower');
    if (f.attending || f.walking) return null;
    switch (f.act) {
      case 'fish':
        return x('fish');
      case 'mend':
        return x('mend', 0, t % 4.1 < 0.15);
      case 'hoe':
        // Up slowly, down hard.
        return x('hoe', t % 1.5 < 0.95 ? 0 : 1);
      case 'sweep':
        return x('sweep', Math.floor(t / 0.55) % 2);
      case 'drink':
        // Now and then the cup goes up to the lips.
        return t % 6 < 1.6 ? x('drink') : set.sit;
      case 'sit':
        return set.sit;
      case 'call':
        return t % 5 < 2.2 ? x('call') : null;
      case 'chat':
        // A hand raised now and then while talking.
        return t % 7 < 1.2 ? x('wave', 1) : null;
    }
    return null;
  }

  /** Standing about: blinking, looking round, and (townsfolk) a stretch now and then. */
  private idleFrame(e: Entity, set: SpriteSet): HTMLCanvasElement | null {
    const x = set.extra;
    if (!x || e.dead) return null;
    const t = this.world.time + ((e.id * 7.31) % 40);
    // A frame not drawn yet is asked for, and the figure goes on breathing meanwhile: its stand-in
    // (the first standing frame) dropped the breath back a step for a frame or two.
    const ready = (img: HTMLCanvasElement) => (img === NOT_DRAWN ? null : img);
    const people = e.kind === 'npc' || e.kind === 'ambient';
    if (people && t % 27 < 1.6) return ready(x('stretch', 0, false, notDrawn));
    const look = t % 9.3;
    if (look < 1.3) return ready(x('glance', Math.floor(t / 9.3) % 2, false, notDrawn));
    // Blinking: the closed eyes for each step of the breath are drawn when first wanted. Shown as
    // they arrived, a blink strobed between open and shut (and the stand-in's step) for a frame at
    // a time — on the front view the whole face flickered. A blink whose frame is not drawn when it
    // starts is skipped (the eyes stay open); one whose next step is missing ends there.
    const n = set.idle.length;
    const cyc = t % BLINK_EVERY;
    if (cyc < BLINK_TIME) {
      const at = Math.floor(t / BLINK_EVERY);
      let b = this.blinks.get(e.id);
      if (!b || b.at !== at) this.blinks.set(e.id, (b = { at, shut: true }));
      if (b.shut) {
        const img = ready(x('idle', Math.abs(Math.floor(e.animT * IDLE_FPS)) % n, true, notDrawn));
        if (img) return img;
        b.shut = false;
      }
    } else {
      this.blinks.delete(e.id);
      if (cyc <= BLINK_EVERY - 0.5) return null;
      // Just before the next blink: ask for the closed eyes it will show.
      const ph = Math.abs(Math.floor((e.animT + BLINK_EVERY - cyc) * IDLE_FPS)) % n;
      x('idle', ph, true, notDrawn);
      x('idle', (ph + 1) % n, true, notDrawn);
    }
    return null;
  }
  /** By entity: the blink under way (its count) and whether it is still shown shut. */
  private readonly blinks = new Map<number, { at: number; shut: boolean }>();

  private readonly propIds = new WeakMap<Prop, number>();
  /** Scenery over the player, each with a round window cut where the player stands (see `peekThrough`). */
  private readonly peeked = new WeakMap<Prop, HTMLCanvasElement>();
  /** How far the window through scenery has opened (0–1): it grows open when the player goes behind. */
  private peek = 0;
  private peekedThisFrame = false;
  private nextPropId = 0;
  private readonly preparedSets = new WeakSet<SpriteSet>();
  private readonly prepareQueue: SpriteSet[] = [];
  private prepareScheduled = false;
  /**
   * The loading screen: asks for a figure's fighting motions (and, for the player, the skill
   * motions) in every view, ahead of need.
   */
  warmAhead(set: SpriteSet, skills: boolean): void {
    for (const s of [set, ...Object.values(set.views ?? {})]) {
      if (!s) continue;
      this.warm(s);
      if (skills) this.warmSkills(s);
    }
  }

  /** The loading screen: queues every view of a figure for upload (`uploadAhead`). */
  prepareAhead(set: SpriteSet): void {
    for (const s of [set, ...Object.values(set.views ?? {})]) if (s) this.queuePreparation(s);
  }

  /** The loading screen: uploads queued figure frames for up to `ms`; returns how many are left. */
  uploadAhead(ms: number): number {
    for (const set of this.prepareQueue.splice(0)) set.prepare?.((image) => this.gpu.prepare(image));
    return this.gpu.uploadPrepared(ms);
  }

  private queuePreparation(set: SpriteSet): void {
    if (this.preparedSets.has(set)) return;
    this.preparedSets.add(set);
    this.prepareQueue.push(set);
    if (this.prepareScheduled) return;
    this.prepareScheduled = true;
    const pump = () => {
      this.prepareQueue.shift()?.prepare?.((image) => this.gpu.prepare(image));
      if (this.prepareQueue.length) setTimeout(pump, 250);
      else this.prepareScheduled = false;
    };
    setTimeout(pump, 250);
  }

  readonly profile = { terrain: 0, figures: 0, effects: 0, gpu: 0, overlay: 0, scenery: 0, characters: 0 };

  render(dt: number): void {
    let mark = performance.now();
    this.profile.scenery = this.profile.characters = 0;
    // The camera turn first, while the world picture still holds last frame's (to fade out of).
    const prev = { ox: this.ox, oy: this.oy, w: this.lowW, h: this.lowH };
    // Lane CAMERA-PITCH: the optional yaw follow behind a click-to-move walk (cameraFollow.ts, setting `cameraFollow`).
    const pl = this.world.player;
    const walk = pl.path?.length && (pl.dirX || pl.dirY) ? this.iso.dirTurn(pl.dirX, pl.dirY) : null;
    this.follow.step(dt, walk, this.turn.control, settings.cameraFollow);
    if (this.turn.update(dt, this.iso, this.cam, !!this.stage, () => this.gpu.keepWorld(), prev))
      this.effects.clearPositioned();
    // The pitch: the camera kept on the player as the ground's depth squashes about the map's middle.
    // Lane CAMERA-PITCH experiment: a little flatter while flying with qinggong (flightTilt.ts, setting `flightTilt`).
    const pitched = this.pitch.step(dt);
    const tilted = this.flightTilt.step(dt, this.world.player.z, settings.flightTilt);
    const pitch = Math.max(PITCH_MIN, this.pitch.pitch + this.flightTilt.offset);
    if (pitched || tilted || pitch !== this.iso.pitch) this.applyPitch(pitch);
    this.duelFrame = this.duelFraming.update(this.world, dt, this.scale);
    this.stepZoom(dt);
    this.gpu.begin();
    this.lookAt.update(this.world, this.iso, (e) => {
      const s = this.sprites.of(e);
      return (s?.h ?? 20) * visualScale(e, s);
    });
    this.crowd = this.dressedSeen;
    this.dressedSeen = 0;
    const g = this.lg;
    g.setTransform(this.res, 0, 0, this.res, 0, 0);
    g.imageSmoothingEnabled = false;
    const w = this.world;
    const fx = this.effects;
    // Indoors (interior.ts): the camera follows the World's map into a building and out again.
    const indoor = this.indoor.sync(w, this.iso, daylightAt(w.hour));
    if (indoor.moved) {
      [this.cam.x, this.cam.y] = this.iso.proj(w.player.x, w.player.y);
      this.effects.clearPositioned();
      this.turn.cut();
    }
    const scene = indoor.scene;
    // Follow the player up roofs and into the air too, so a jump never carries them off screen.
    const [px, gy] = this.iso.proj(w.player.x, w.player.y);
    const py = gy - fx.anim(w.player.id).elev - fx.hoverOf(w.player) - w.player.z;
    // Locked onto the player: every height (roofs, jumps, flight) followed fast enough that the
    // player stays in the middle of the screen.
    const k = 1 - Math.exp(-dt * 12);
    const focus = this.stage?.focus;
    // Always on the player, in a bout too (user 10-11, 9yin-like; the bout only zooms out, duelFraming.ts); a bout
    // lifts the view a little so the player sits above the dock.
    const tx = px;
    const ty = py;
    let lift = 0;
    const df = this.duelFrame;
    if (df.foe && df.blend > 0 && !focus) {
      const sp = dockSpace(this.h);
      lift = (((sp.bottom - sp.top) / 2) / this.px) * df.blend;
    }
    // Lane CAMERA-PITCH: near the ground the player is framed lower, so the sky over the horizon shows.
    if (!scene) lift -= pitchDrop(this.iso.pitch, this.lowH);
    if (focus) {
      // A cutscene's camera: it eases its own pans, so it is followed exactly.
      [this.cam.x, this.cam.y] = this.iso.proj(focus.x, focus.y);
    } else {
      this.cam.x = lerp(this.cam.x, tx, k);
      this.cam.y = lerp(this.cam.y, ty, k);
    }
    const shake = settings.shake ? fx.shake : 0;
    // Rounded as the player's whole pixel plus the camera's lag behind it, not as the camera alone:
    // rounded on its own, the camera stepped out of phase with the player's own rounding, so a
    // walking figure shook a pixel back and forth on screen.
    this.ox = Math.round(this.lowW / 2 - (this.cam.x - px) + shake * (Math.random() - 0.5)) - Math.round(px);
    // Aim at the middle of the player's figure, not their knees, so zooming in closes in on the
    // whole body rather than pushing the head off the top of the screen.
    const figure = this.sprites.of(w.player);
    const mid = Math.round(((figure?.h ?? 44) * visualScale(w.player, figure)) / 2);
    this.oy =
      Math.round(this.lowH / 2 - (this.cam.y - gy) + mid - lift + shake * (Math.random() - 0.5)) - Math.round(gy);
    // Globe View (`?globe=1`, globe.ts): bent round the player's ground point, outdoors only.
    globeFrame(!scene, px + this.ox, gy + this.oy, this.lowW, this.lowH);
    const { ox, oy } = this;

    g.imageSmoothingEnabled = false;
    // Indoors: the room's floor in the dark instead of the sky and the ground.
    const outdoors = scene ? null : this.terrain();
    const frame: Writable<PassFrame> = {
      renderer: this,
      world: w,
      effects: fx,
      sprites: this.sprites,
      iso: this.iso,
      gpu: this.gpu,
      g,
      mg: this.mg,
      dt,
      ox,
      oy,
      W: this.lowW,
      H: this.lowH,
      screenW: this.w,
      screenH: this.h,
      px: this.px,
      cam: this.cam,
      room: this.room(),
      visible: this.visible(),
      player: [px, gy] as const,
      indoor: this.indoor,
      scene,
      outdoors,
      crowd: this.crowd,
      cine: 0,
      shown: () => this.shown(),
      drawEntity: (e: Entity) => this.drawEntity(e, ox, oy),
    };
    // Under the scenery and the figures: the sky or a room's floor, the ground, the marks on it
    // (warp circles, targets, the cursor), scenery shadows and ground effects (render/passes).
    this.passes.draw('ground', frame);

    /** `lx`, `ly`: where a prop stands at the camera's whole angle, past where it is painted. */
    type Item = {
      d: number;
      prop?: Prop;
      pw?: number;
      ph?: number;
      ent?: Entity;
      lx?: number;
      ly?: number;
      strip?: TurnedStrip;
    };
    const list: Item[] = [];
    const vx0 = -ox - 40;
    const vx1 = -ox + this.lowW + 40;
    // Lane CAMERA-PITCH: at a low pitch only a slice round the player, horizon to just in front (pitchView.ts).
    const band = scene ? null : pitchBand(this.iso, this.iso.depth(w.player.x, w.player.y), gy, oy, this.lowH);
    const vy0 = Math.max(-oy - 70, band ? Math.floor(band.farY) - 2 : -Infinity);
    const vy1 = Math.min(-oy + this.lowH + 70, band ? Math.ceil(band.nearY) + 2 : Infinity);
    // Between the painted quarter turns: the part of the painted view that turns onto the screen.
    const [qx0, qx1, qy0, qy1] = this.iso.viewRect(vx0, vx1, vy0, vy1);
    // Tall grass sways in the wind and parts round anyone on foot wading through it.
    const waders: Wader[] = [];
    for (const e of this.shown()) {
      if (e.dead || e.z > 4 || hidden(e)) continue;
      const [x, y] = this.iso.turn(e.x, e.y);
      waders.push({ id: e.id, x, y });
    }
    outdoors?.grass.update(dt, w.time, waders, settings.ambience, { x0: qx0, x1: qx1, y0: qy0, y1: qy1 });
    // C0-ROTATE-PORT: turned or tilted outdoors, buildings and stone blocks are drawn from their painted views at the
    // camera's angle (render/turned/); at the painted angle and pitch they stay the painted props.
    const turnedOut = !scene && this.iso.warped;
    this.turned.frame(this.iso);
    // Only the props near the view, found by screen cell rather than by scanning the whole map.
    for (const grid of scene ? [scene.props] : [outdoors!.props, outdoors!.grass.grid])
      for (const i of grid.query(qx0, qx1, qy0, qy1)) {
        const pr = grid.props[i]!;
        // Drawn turned below instead (its prop stays for the shadows it throws).
        if (turnedOut && this.turned.take(pr, this.iso.origin)) continue;
        const pw = grid.width(i);
        const ph = grid.height(i);
        if (!this.iso.warped) {
          list.push({ d: pr.d, prop: pr, pw, ph });
          continue;
        }
        const [fx, fy] = pr.foot ?? [pr.x + pw / 2, pr.y + ph];
        if (scene) {
          // A room's walls and furniture turn with its floor, as one picture (see `flushProps`).
          list.push({ d: pr.d + this.iso.liftDepth(fx, fy), prop: pr, pw, ph });
          continue;
        }
        // Outdoors, turned: standing upright where its foot (a building's middle) has turned to.
        const [tx, ty] = this.iso.lift(fx, fy);
        const lx = Math.round(tx - fx);
        const ly = Math.round(ty - fy);
        if (pr.x + lx > vx1 || pr.x + pw + lx < vx0 || pr.y + ly > vy1 || pr.y + ph + ly < vy0) continue;
        list.push({ d: pr.d + this.iso.liftDepth(fx, fy), prop: pr, pw, ph, lx, ly });
      }
    // C0-ROTATE: buildings and stone blocks at the camera's angle, in strips sorted with everything else;
    // the pixels on no face of a building (ridge ornaments, spires) stand upright at its middle.
    if (turnedOut) {
      const { strips, rests } = this.turned.strips(vx0, vx1, vy0, vy1);
      for (const st of strips) list.push({ d: st.d, strip: st });
      for (const piece of rests) {
        const pr = piece.rest!;
        const [fx, fy] = pr.foot!;
        const [tx, ty0] = this.iso.lift(fx, fy);
        // Pitched, the building's height changes: the ornament rides up or down with its roof.
        const ty = ty0 - (this.iso.heightK - 1) * (fy - (pr.y + pr.img.height / 2));
        list.push({
          d: piece.frontD + 0.02,
          prop: pr,
          pw: pr.img.width,
          ph: pr.img.height,
          lx: Math.round(tx - fx),
          ly: Math.round(ty - fy),
        });
      }
    }
    // Riders seated in a carriage (carriage.ts) are inside it, not drawn.
    const aboard = w.carriage.aboard();
    for (const e of this.shown()) {
      if (e.kind === 'monster' && e.dead && e.deadT > 0.6) continue;
      if (hidden(e) || aboard.includes(e.id)) continue;
      // Far off screen: not drawn (the big maps hold many more monsters).
      const [ex, ey] = this.iso.proj(e.x, e.y);
      if (ex < vx0 - 80 || ex > vx1 + 80 || ey < vy0 - 40 || ey > vy1 + 160) continue;
      // Up on a roof, a fighter is drawn over the whole building beneath them; on a terrace (its
      // platform, its stairs, the foot of its wall), past the terrace's nearest corner.
      let d = this.iso.depth(e.x, e.y) + (groundAt(w.map, e.x, e.y) > 40 ? 9 : 0);
      // A coach is long: sorted by the point between its cabin and its horse.
      if (e.sprite === 'carriage') d = this.iso.depth(e.x + e.dirX * 1.3, e.y + e.dirY * 1.3);
      const st = structureAt(w.map, Math.floor(e.x), Math.floor(e.y));
      if (st?.kind === 'terrace')
        d = Math.max(
          d,
          0.01 +
            Math.max(
              ...[
                [st.i, st.j],
                [st.i + st.w, st.j],
                [st.i, st.j + st.h],
                [st.i + st.w, st.j + st.h],
              ].map(([x, y]) => this.iso.depth(x!, y!)),
            ),
        );
      // C0-ROTATE: set right against the buildings drawn turned that it overlaps (render/turned/).
      if (turnedOut && !e.z) d = this.turned.figureDepth(d, e.x, e.y, ex, ey);
      list.push({ d, ent: e });
    }
    cutToBand(list, band, (it) => (it.strip ? it.strip.piece.frontD : it.d));
    // The graphics tier's figure LOD (graphics.ts): past its crowd, the farthest are left out.
    cullCrowd(list, w.player);
    list.sort((a, b) => a.d - b.d);
    this.profile.terrain = performance.now() - mark;
    mark = performance.now();
    // Where the player stands on screen (world pixels), to see them through scenery in front.
    const me = this.playerBox();
    // C0-ROTATE: a building with a strip in front of the player and over them opens a window there, easing in and
    // out: its strips over the player fade (as the painted props' window, `peekThrough`), the rest stay solid.
    for (const piece of this.turned.all()) piece.hides = false;
    {
      let after = false;
      for (const it of list) {
        if (it.ent === w.player) after = true;
        else if (it.strip) {
          const st = it.strip;
          st.peek = !!me && after && st.x0 < me.x1 && st.x1 > me.x0 && st.y0 < me.y1 && st.y1 > me.y0;
          if (st.peek) st.piece.hides = true;
        }
      }
    }
    for (const piece of this.turned.all())
      piece.fade += ((piece.hides ? 1 : 0) - piece.fade) * (1 - Math.exp(-dt * 8));
    this.turned.begin();
    let meshRun: TurnedStrip[] = [];
    let meshAlpha = 1;
    const flushMesh = () => {
      if (!meshRun.length) return;
      const mesh = this.turned.mesh(meshRun, meshAlpha, g.getTransform(), ox, oy);
      if (mesh) this.gpu.world.node(mesh);
      meshRun = [];
    };
    // The window through scenery opens over a fraction of a second, and shuts at once when clear.
    this.peek = this.peekedThisFrame ? Math.min(1, this.peek + dt * 5) : 0;
    this.peekedThisFrame = false;
    let past = false;
    let run: SceneImage[] = [];
    let firstId = 0,
      runs = 0;
    const flushProps = () => {
      if (!run.length) return;
      const batchStart = performance.now();
      // Indoors, turned: the room's pieces are turned on screen with its floor (render/passes/turn.ts).
      const warp = !!scene && this.iso.warped;
      if (warp) {
        g.save();
        g.transform(...this.iso.groundTransform(ox, oy));
      }
      // Turned, the props sort differently every frame: their batches are reused by place instead.
      const key = this.iso.warped
        ? `spin:${runs++}:${run.length}`
        : // By its first prop only (lane C0-P1): as the view moves a run grows and shrinks, and keying by its end and
          // length too made a new batch nearly every frame on a big map (webgl.ts hides a kept batch's extra sprites).
          `${this.iso.rot}:${firstId}`;
      this.gpu.world.batch(key, run, ox, oy);
      if (warp) g.restore();
      this.profile.scenery += performance.now() - batchStart;
      run = [];
    };
    for (const it of list) {
      if (it.strip) {
        flushProps();
        const st = it.strip;
        // Quantised so a fading building still draws in a few runs.
        const alpha = past && st.peek ? 1 - (Math.round(st.piece.fade * 8) / 8) * 0.6 : 1;
        if (meshRun.length && (meshRun[0]!.piece.tex !== st.piece.tex || alpha !== meshAlpha)) flushMesh();
        meshAlpha = alpha;
        meshRun.push(st);
        continue;
      }
      flushMesh();
      if (it.prop) {
        const pr = it.prop;
        const pw = it.pw!;
        const ph = it.ph!;
        const px = pr.x + (it.lx ?? 0);
        const py = pr.y + (it.ly ?? 0);
        // Tall scenery drawn after the player and covering them turns see-through.
        const hides = past && me && ph > 28 && px < me.x1 && px + pw > me.x0 && py < me.y1 && py + ph > me.y0;
        if (hides) {
          flushProps();
          this.peekedThisFrame = true;
          const [sx, sy] = [px - pr.x, py - pr.y];
          const at = sx || sy ? { x0: me.x0 - sx, x1: me.x1 - sx, y0: me.y0 - sy, y1: me.y1 - sy } : me;
          g.drawImage(this.peekThrough(pr, pw, ph, at), px + ox, py + oy, pw, ph);
        } else {
          let id = this.propIds.get(pr);
          if (id === undefined) this.propIds.set(pr, (id = ++this.nextPropId));
          if (!run.length) firstId = id;
          run.push({ img: pr.img, x: px, y: py, width: pw, height: ph });
        }
      } else if (it.ent) {
        flushProps();
        const characterStart = performance.now();
        this.drawEntity(it.ent, ox, oy);
        this.profile.characters += performance.now() - characterStart;
        if (it.ent.folk?.lantern) this.drawLantern(it.ent, ox, oy);
        if (it.ent === w.player) past = true;
      }
    }
    flushMesh();
    flushProps();
    this.profile.figures = performance.now() - mark;
    mark = performance.now();
    const cine = (frame.cine = fx.cine);
    // Over the figures: the cinematic dim, a cutscene's and the moves' effects, the light of the
    // hour and the outdoor ambience (render/passes).
    this.passes.draw('world', frame);
    // Camera turn: the last painted quarter turn's picture fades out, turning on with the ground.
    this.turn.drawFade(g, this.iso, ox, oy, this.lowW, this.lowH);

    const mg = this.mg;
    mg.setTransform(1, 0, 0, 1, 0, 0);
    mg.imageSmoothingEnabled = false;
    // Ultimates: the camera closes in on the caster, easing in and back out.
    const want = fx.cineZoom ? cine * 0.3 : 0;
    this.closeIn += (want - this.closeIn) * (1 - Math.exp(-dt * (want > this.closeIn ? 9 : 4)));
    // Camera punch: a brief zoom-in kick on heavy blows.
    const zoom = (1 + (settings.shake ? fx.punch : 0) * 0.045) * (1 + this.closeIn);
    // The picture's centre (the camera) on the screen's centre, at the zoom's pixel scale: the
    // picture is drawn at a whole step and stretched by the fraction, about the same point.
    const bx = this.w / 2 - (this.lowW / 2) * this.px;
    const by = this.h / 2 - (this.lowH / 2) * this.px;
    // Zoom about a point between the screen centre and the caster, by how far in the camera is.
    const hero = w.get(fx.cineSrc);
    const pull = hero && this.closeIn > 0.001 ? Math.min(1, this.closeIn / 0.15) : 0;
    const [hx, hy] = hero ? this.iso.proj(hero.x, hero.y) : [0, 0];
    const fxp = lerp(this.w / 2, (hx + ox) * this.px + bx, pull);
    const fyp = lerp(this.h / 2, (hy + oy - 16) * this.px + by, pull);
    this.lens = { z: zoom, x: bx * zoom + fxp * (1 - zoom), y: by * zoom + fyp * (1 - zoom) };
    const W = this.lowW * this.px * this.dpr;
    const H = this.lowH * this.px * this.dpr;
    this.profile.effects = performance.now() - mark;
    mark = performance.now();
    this.gpu.drawWorld();
    // Sharp-bilinear (webgl.ts): hard texels at any fractional scale, no uneven columns.
    this.gpu.blitWorld(this.lens.x * this.dpr, this.lens.y * this.dpr, W * zoom, H * zoom);
    mg.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    // Over the whole screen: the vignette, flashes, frost and the letterbox (render/passes).
    this.passes.draw('screen', frame);
    // Name tags and numbers follow the zoomed world.
    const v = this.lens;
    mg.setTransform(this.dpr * v.z, 0, 0, this.dpr * v.z, v.x * this.dpr, v.y * this.dpr);
    this.profile.gpu = performance.now() - mark;
    mark = performance.now();
    this.passes.draw('labels', frame);
    mg.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    // Last, unzoomed: a door's fade and a power move's stamp (render/passes).
    this.passes.draw('top', frame);
    this.gpu.present();
    this.profile.overlay = performance.now() - mark;
  }

  /** The player's figure on screen (world pixels, without the camera offset), or null if dead. */
  /**
   * Scenery in front of the player with a round window cut through it, centred on the player's
   * body: nearly clear inside, with a stepped pixel edge (two bands of fading) rather than a soft
   * blur, so the player reads through a wall or a tree while the rest of it stays solid.
   */
  private peekThrough(
    pr: Prop,
    pw: number,
    ph: number,
    me: { x0: number; y0: number; x1: number; y1: number },
  ): HTMLCanvasElement {
    let c = this.peeked.get(pr);
    if (!c || c.width !== pw || c.height !== ph) this.peeked.set(pr, (c = canvas(pw, ph)));
    const g = ctx2d(c);
    g.globalCompositeOperation = 'copy';
    g.drawImage(pr.img, 0, 0, pw, ph);
    g.globalCompositeOperation = 'destination-out';
    const ease = 1 - (1 - this.peek) ** 3;
    const r = Math.max(10, (me.y1 - me.y0) * 0.5 + 6) * ease;
    const cx = Math.round((me.x0 + me.x1) / 2 - pr.x);
    const cy = Math.round((me.y0 + me.y1) / 2 - pr.y);
    // Hard-edged rings, outermost first: each cut adds to the ones before it.
    for (const [k, a] of [
      [1, 0.35],
      [0.86, 0.45],
      [0.7, 0.75],
    ] as const) {
      const rr = Math.round(r * k);
      if (rr < 1) continue;
      g.fillStyle = `rgb(0 0 0 / ${a})`;
      // Row by row, whole pixels wide, so the circle steps like pixel art.
      for (let dy = -rr; dy <= rr; dy++) {
        const half = Math.floor(Math.sqrt(rr * rr - dy * dy));
        g.fillRect(cx - half, cy + dy, half * 2 + 1, 1);
      }
    }
    g.globalCompositeOperation = 'source-over';
    touchCanvas(c);
    return c;
  }

  private playerBox(): { x0: number; y0: number; x1: number; y1: number } | null {
    const p = this.world.player;
    const set = this.sprites.of(p);
    if (!set || p.dead) return null;
    const [sx, sy] = this.iso.proj(p.x, p.y);
    const feet = sy - p.z - this.effects.hoverOf(p) - this.effects.anim(p.id).elev;
    const h = set.h * visualScale(p, set);
    return { x0: sx - 10, x1: sx + 10, y0: feet - h, y1: feet };
  }

  private ellipse(x: number, y: number, rx: number, ry: number): void {
    ellipse(this.lg, x, y, rx, ry);
  }

  /**
   * The flying sword lying flat under the rider's feet along their heading: a qi glow beneath, the
   * blade with a bright ridge, a gold guard, the dark hilt and a red tassel streaming behind.
   */
  /**
   * พัดกระบี่ม่วงราตรี: four swords in violet scabbards fanned in a V, hilts up, on an ornate
   * crescent mount; silver winged guards with jade pommels, bead chains strung between the
   * scabbards, a jewel at the centre and two white tassels hanging below. A violet glow breathes
   * around it.
   */
  private drawBackSwords(x: number, y: number, e: Entity, h: number, facing: number, alpha: number): void {
    const g = this.lg;
    const t = this.world.time;
    const sk = SKINS['violet-fan-swords']!;
    const side = [0, 4].includes(this.iso.heading(e)) ? -facing * 3 : 0;
    const bx = Math.round(x + side);
    const by = Math.round(y - h * 0.36);
    const pulse = 0.5 + 0.5 * Math.sin(t * 2.6);
    const line = (x0: number, y0: number, x1: number, y1: number, w: number, col: string) => {
      g.strokeStyle = col;
      g.lineWidth = w;
      g.beginPath();
      g.moveTo(x0, y0);
      g.lineTo(x1, y1);
      g.stroke();
    };
    g.save();
    g.globalAlpha = alpha;
    g.lineCap = 'butt';
    // Violet glow behind the whole piece.
    const glow = g.createRadialGradient(bx, by - 16, 2, bx, by - 16, 30);
    glow.addColorStop(0, `rgba(${sk.color},${0.28 + 0.14 * pulse})`);
    glow.addColorStop(1, `rgba(${sk.color},0)`);
    g.fillStyle = glow;
    g.fillRect(bx - 32, by - 48, 64, 60);

    // Scabbards and hilts: base point, tilt, length.
    // Spread wide enough that the hilts stand either side of the head, not over it.
    const blades = [
      [-6, -0.85, 22],
      [-3, -0.42, 25],
      [3, 0.42, 25],
      [6, 0.85, 22],
    ] as const;
    const tops: [number, number][] = [];
    for (const [ox, ang, len] of blades) {
      const [dx, dy] = [Math.sin(ang), -Math.cos(ang)];
      const x0 = bx + ox;
      const y0 = by - 2;
      const x1 = x0 + dx * len;
      const y1 = y0 + dy * len;
      tops.push([x0 + dx * len * 0.62, y0 + dy * len * 0.62]);
      // Scabbard: dark edge, violet body, a lit stripe.
      line(x0, y0, x1, y1, 4, '#26143e');
      line(x0, y0, x1, y1, 3, '#5a3a9a');
      line(x0 - dy * 0.8, y0 + dx * 0.8, x1 - dy * 0.8, y1 + dx * 0.8, 1, '#9a7ade');
      // Silver bands with studs.
      for (const f of [0.3, 0.68]) {
        const [mx, my] = [x0 + dx * len * f, y0 + dy * len * f];
        line(mx - dy * 2, my + dx * 2, mx + dy * 2, my - dx * 2, 1.5, '#d8d8e8');
        g.fillStyle = '#3a7a6a';
        g.fillRect(Math.round(mx) - 0, Math.round(my) - 0, 1, 1);
      }
      // Winged silver guard at the mouth, the grip, and a jade pommel.
      const [gx, gy] = [x1, y1];
      line(gx - dy * 4, gy + dx * 4, gx + dy * 4, gy - dx * 4, 2, '#e4e4f0');
      line(gx - dy * 4 + dx * 1.5, gy + dx * 4 + dy * 1.5, gx - dy * 2, gy + dx * 2, 1, '#b0b0c8');
      line(gx + dy * 4 + dx * 1.5, gy - dx * 4 + dy * 1.5, gx + dy * 2, gy - dx * 2, 1, '#b0b0c8');
      line(gx, gy, gx + dx * 7, gy + dy * 7, 2, '#2a1a3a');
      for (const f of [2, 4, 6]) {
        g.fillStyle = '#6a5a8a';
        g.fillRect(Math.round(gx + dx * f), Math.round(gy + dy * f), 1, 1);
      }
      const [px, py] = [gx + dx * 8.5, gy + dy * 8.5];
      g.fillStyle = '#e4e4f0';
      g.fillRect(Math.round(px) - 1, Math.round(py) - 1, 3, 3);
      g.fillStyle = pulse > 0.5 ? '#7ffff0' : '#3fe0c8';
      g.fillRect(Math.round(px), Math.round(py) - 1, 1, 2);
    }
    // Bead chains sagging between the scabbards.
    for (let k = 0; k < tops.length - 1; k++) {
      const [ax0, ay0] = tops[k]!;
      const [ax1, ay1] = tops[k + 1]!;
      for (let q = 0; q <= 6; q++) {
        const f = q / 6;
        const sag = Math.sin(f * Math.PI) * 3;
        g.fillStyle = q % 2 ? '#2f6a58' : '#7fd8c0';
        g.fillRect(Math.round(ax0 + (ax1 - ax0) * f), Math.round(ay0 + (ay1 - ay0) * f + sag), 1, 1);
      }
    }
    // The crescent mount: violet with silver rims, curled ends, and a jewel in the middle.
    for (let i = -11; i <= 11; i++) {
      const curve = Math.round((i * i) / 30);
      const top = by - 3 - curve;
      g.fillStyle = '#4a2a7a';
      g.fillRect(bx + i, top, 1, 6);
      g.fillStyle = '#d8d8e8';
      g.fillRect(bx + i, top - 1, 1, 1);
      g.fillRect(bx + i, top + 6, 1, 1);
      if (Math.abs(i) % 4 === 2) {
        g.fillStyle = '#c99ae8';
        g.fillRect(bx + i, top + 2, 1, 2);
      }
    }
    for (const sx of [-1, 1]) {
      g.fillStyle = '#e4e4f0';
      g.fillRect(bx + sx * 12, by - 9, 1, 3);
      g.fillRect(bx + sx * 13, by - 10, 1, 2);
    }
    g.fillStyle = '#e4e4f0';
    g.fillRect(bx - 2, by - 1, 5, 4);
    g.fillStyle = '#3fe0c8';
    g.fillRect(bx - 1, by, 3, 2);
    g.fillStyle = '#ffffff';
    g.fillRect(bx, by, 1, 1);
    // Two white tassels on jade beads, swaying.
    for (const sx of [-6, 6]) {
      const sway = Math.round(Math.sin(t * 2 + sx) * 1);
      const [tx, ty] = [bx + sx, by + 4 - Math.round((sx * sx) / 30)];
      g.fillStyle = '#c8b070';
      g.fillRect(tx, ty, 1, 2);
      g.fillStyle = '#3fe0c8';
      g.fillRect(tx - 1, ty + 2, 2, 2);
      g.fillStyle = '#c8b070';
      g.fillRect(tx, ty + 4, 1, 1);
      g.fillStyle = '#f2eef8';
      g.fillRect(tx - 1 + sway, ty + 5, 3, 7);
      g.fillStyle = '#c8c0d8';
      g.fillRect(tx + sway, ty + 7, 1, 5);
    }
    g.restore();
  }

  /**
   * The Blood Sabre school's great sabre, slung level across the back: a broad curved blade
   * stained with blood, a red-wrapped hilt and a ring pommel. Blood drips from it (see Effects).
   */
  private drawGreatSabre(x: number, y: number, h: number, facing: number, alpha: number): void {
    const g = this.lg;
    const f = facing < 0 ? -1 : 1;
    const by = Math.round(y - h * 0.52);
    const X = (dx: number) => Math.round(x + dx * f);
    g.save();
    g.globalAlpha = alpha;
    // The blade, grip and guard never change: drawn once per facing, then placed as one image.
    const img = greatSabreImage(f);
    g.drawImage(img, Math.round(x) - (f > 0 ? SABRE_LEFT : SABRE_W - 1 - SABRE_LEFT), by - SABRE_TOP);
    g.strokeStyle = '#c8a040';
    g.lineWidth = 1;
    g.beginPath();
    g.arc(X(-25), by + 0.5, 2.5, 0, Math.PI * 2);
    g.stroke();
    g.restore();
  }

  /**
   * นกอินทรีทะยานฟ้า: a great eagle under the rider's feet, wings spread wide and beating, white
   * head and hooked yellow beak toward the heading, a fanned tail behind.
   */
  /**
   * The eagle under the rider, wings beating, in eight directions: head-on when heading toward
   * the viewer, from behind when heading away, side-on (mirrored) otherwise.
   */
  private drawEagle(x: number, y: number, e: Entity, scale: number, palette: EaglePalette): void {
    const g = this.lg;
    const v = eagleViews(palette);
    // Eight directions: five drawn views, mirrored for the other three (as the figures are).
    const [view, mirror] = EAGLE_VIEW[this.iso.heading(e)]!;
    const sp = v[view];
    const rate = e.moving ? 11 : 6;
    const img = finishArt(
      sp.frames[Math.floor(this.world.time * rate) % sp.frames.length]!,
      true,
      'feathers',
    );
    const face = mirror;
    const s = scale * 1.25;
    g.save();
    g.translate(Math.round(x), Math.round(y + 3));
    if (face < 0) g.scale(-1, 1);
    g.drawImage(img, (artX(img) - sp.ax) * s, (artY(img) - sp.ay) * s, artW(img) * s, artH(img) * s);
    g.restore();
  }

  private drawFlyingSword(x: number, y: number, e: Entity, s: number): void {
    const g = this.lg;
    const t = this.world.time;
    const ha = (this.iso.heading(e) * Math.PI) / 4;
    // Along the blade (screen space, flattened for the iso ground) and across it.
    const [ux, uy] = [Math.cos(ha), Math.sin(ha) * 0.5];
    const ul = Math.hypot(ux, uy);
    const [px, py] = [-uy / ul, ux / ul];
    const L = 30 * s;
    const at = (f: number, side = 0): [number, number] => [
      x + ux * L * f + px * side,
      y + 1 + uy * L * f + py * side,
    ];
    const line = (a: [number, number], b: [number, number], w: number, col: string) => {
      g.strokeStyle = col;
      g.lineWidth = w;
      g.beginPath();
      g.moveTo(a[0], a[1]);
      g.lineTo(b[0], b[1]);
      g.stroke();
    };
    g.save();
    g.lineCap = 'round';
    // Qi glow under the blade, pulsing.
    const pulse = 0.5 + 0.5 * Math.sin(t * 6);
    g.fillStyle = `rgba(120,215,255,${0.18 + 0.12 * pulse})`;
    g.beginPath();
    g.ellipse(
      x + ux * L * 0.08,
      y + 2 + uy * L * 0.08,
      L * 0.55 * Math.max(0.45, Math.abs(ux)) + 3,
      4 * s,
      0,
      0,
      Math.PI * 2,
    );
    g.fill();
    const tip = at(0.55);
    const guard = at(-0.2);
    const pommel = at(-0.45);
    // Blade: steel with a dark edge, then the bright ridge running to the point.
    line(guard, tip, 3.4 * s, '#5d7286');
    line(guard, at(0.5), 2.2 * s, '#d6e3ee');
    line(at(-0.18, -0.5), at(0.46, -0.5), 1, '#ffffff');
    // Guard across the blade, hilt and pommel.
    line(at(-0.2, -3.5 * s), at(-0.2, 3.5 * s), 2.2 * s, '#c9922f');
    line(at(-0.2, -2.5 * s), at(-0.2, 2.5 * s), 1, '#f3cf6e');
    line(guard, pommel, 2 * s, '#3a2620');
    g.fillStyle = '#e0aa45';
    g.fillRect(Math.round(pommel[0] - 1), Math.round(pommel[1] - 1), 2, 2);
    // Tassel streaming behind, waving faster when moving.
    const wave = t * (e.moving ? 18 : 6);
    let [qx, qy] = pommel;
    for (let k = 1; k <= 4; k++) {
      const side = Math.sin(wave - k * 0.9) * (1 + k * 0.6);
      const [nx, ny] = at(-0.45 - k * 0.07, side);
      line([qx, qy], [nx, ny], 1.5, k % 2 ? '#c8443b' : '#e2675a');
      [qx, qy] = [nx, ny];
    }
    g.restore();
  }

  /**
   * รถม้า (render/carriage.ts): the coach and its horse, wheels turned and the horse stepping by
   * the ground the coach has rolled (shared carriage.ts), a rider at the window while one is aboard;
   * or the station's signpost and hitching rail.
   */
  private drawCarriage(e: Entity, ox: number, oy: number): void {
    const [sx, sy] = this.iso.proj(e.x, e.y);
    const heading = this.iso.heading(e);
    let img: HTMLCanvasElement;
    let mirror: 1 | -1;
    if (e.sprite === 'carriageSign') {
      const [view, m] = carriageView(heading);
      img = carriageSignFrame(view);
      mirror = m;
    } else {
      const c = this.world.carriage;
      const mine = c.coach === e;
      const shot = carriagePose(
        heading,
        mine ? c.rolled : 0,
        mine ? c.speed : 0,
        this.world.time + e.id * 0.37,
        mine && c.aboard().length > 0,
      );
      img = carriageFrame(shot.pose);
      mirror = shot.mirror;
    }
    const g = this.lg;
    g.save();
    g.translate(Math.round(sx + ox), Math.round(sy + oy));
    if (mirror < 0) g.scale(-1, 1);
    g.drawImage(finishArt(img, true), -CARRIAGE_AX, -CARRIAGE_AY);
    g.restore();
  }

  private readonly shadowTilts = new WeakMap<
    Entity,
    { x: number; y: number; rot: number; radius: number; tilt: number }
  >();

  /** A figure's contact and cast shadows (render/lighting.ts), tilted to lie along a stair or roof. */
  private drawShadows(
    e: Entity,
    set: SpriteSet,
    facing: number,
    x: number,
    ground: number,
    sx: number,
    sy: number,
    s: number,
    lift: number,
    a: number,
  ): void {
    const srx = contactRadius(set, s) * (1 - lift);
    let cachedTilt = this.shadowTilts.get(e);
    if (
      !cachedTilt ||
      cachedTilt.x !== e.x ||
      cachedTilt.y !== e.y ||
      cachedTilt.rot !== this.iso.rot ||
      cachedTilt.radius !== srx
    ) {
      const rise = (dx: number) => groundAt(this.world.map, ...this.iso.unproj(sx + dx, sy));
      cachedTilt = {
        x: e.x,
        y: e.y,
        rot: this.iso.rot,
        radius: srx,
        tilt: (rise(-srx) - rise(srx)) / (2 * srx),
      };
      this.shadowTilts.set(e, cachedTilt);
    }
    const air = e.z + Math.round(this.effects.hoverOf(e));
    const hour = this.world.interiors.where ? null : this.world.hour;
    const g = this.lg;
    // Between the painted quarter turns the shadow lies on the turned ground, as the scenery's do.
    const spun = this.iso.warped;
    if (spun) {
      const [m0, m1, m2, m3] = this.iso.spinMatrix();
      g.save();
      g.transform(m0, m1, m2, m3, x - (m0 * x + m2 * ground), ground - (m1 * x + m3 * ground));
    }
    drawFigureShadows(g, set, facing, x, ground, s, hour, lift, air, a, cachedTilt.tilt);
    if (spun) g.restore();
  }

  /**
   * C0-ROTATE pitch: a figure drawn for 30° stands taller as the view flattens (heights go with
   * cos(pitch)): the whole figure, its shadow and its effects, scaled up the screen about its foot.
   * From 30° up it stays as painted (`figureK`, lane CAMERA-PITCH: a billboard, never squashed).
   */
  private drawEntity(e: Entity, ox: number, oy: number): void {
    const k = this.iso.figureK;
    if (k === 1) return this.drawEntityAt(e, ox, oy);
    const fy = Math.round(this.iso.proj(e.x, e.y)[1] + oy);
    const g = this.lg;
    g.save();
    g.translate(0, fy);
    g.scale(1, k);
    g.translate(0, -fy);
    this.drawEntityAt(e, ox, oy);
    g.restore();
  }

  private drawEntityAt(e: Entity, ox: number, oy: number): void {
    if (e.sprite === 'carriage' || e.sprite === 'carriageSign') return this.drawCarriage(e, ox, oy);
    // Most emotes are struck with the weapon back in its scabbard; so are a bare-handed art's moves (render/longMove.ts).
    const sheathe =
      ((e.emote && !EMOTES[e.emote]?.armed) || (bareHandedMove(this.world, e) && !!this.sprites.of(e)?.armed)) &&
      !e.outfit.includes('sheathed');
    const base = this.sprites.of(
      sheathe ? { sprite: e.sprite, outfit: [...e.outfit, 'sheathed'], id: e.id } : e,
    );
    if (!base) return;
    // Turned round to face the target it is looking at (render/lookAt.ts), else its own heading.
    const turned = this.lookAt.viewOf(e, this.iso);
    const { set, facing } = viewOf(
      base,
      turned?.heading ?? this.iso.heading(e),
      turned?.facing ?? this.iso.facing(e),
    );
    this.queuePreparation(set);
    const g = this.lg;
    const [sx, sy] = this.iso.proj(e.x, e.y);
    let x = Math.round(sx + ox);
    // Standing up on a boulder lifts the whole figure, shadow included.
    const ground = Math.round(sy + oy - this.effects.stepElevation(e));
    // Airborne entities are drawn above their ground shadow.
    let y = ground - Math.round(e.z);
    const s = visualScale(e, set);
    let a = 1;
    if (e.kind === 'monster' && e.dead) a = 1 - e.deadT / 0.6;
    // A fallen player ghosts to 0.35, once the fall (render/hitReact.ts) has been seen.
    if (e.kind === 'player' && e.dead) a = hitReactGhost(this.world.time - this.effects.anim(e.id).deathAt);
    if (a <= 0) return;

    // A mount lifts the rider a little off the ground.
    const hover = Math.round(this.effects.hoverOf(e));
    y -= hover;
    const lift = Math.min(0.5, (e.z + hover) / 60);
    // Ground shadows (render/lighting.ts): fitted to the figure's stance and height, a contact
    // shadow and a cast shadow for the hour, tilted along a stair or roof by the ground's rise.
    if (lightingOn()) this.drawShadows(e, set, facing, x, ground, sx, sy, s, lift, a);
    g.globalAlpha = a;
    // Auras (render/auras): the sigil on the ground and what rises behind the body.
    const auras = aurasOf(this.world, e);
    const aura = auraRim(auras);
    drawAuras(g, auras, 'ground', x, ground, s, this.world.time, a, e.id);
    drawAuras(g, auras, 'back', x, y, s, this.world.time, a, e.id);
    if ((e.pk || e.title === 'หัวหน้า') && !e.dead) {
      // เปิดแดง (and the elite's tag, L6): a pool of blood spreading and pulsing underfoot.
      const pulse = 0.5 + 0.5 * Math.sin(this.world.time * 4);
      g.fillStyle = `rgba(150,8,16,${0.35 + 0.15 * pulse})`;
      this.ellipse(x, ground, 15 * s, 6 * s);
      g.fill();
      g.strokeStyle = `rgba(255,40,50,${0.5 + 0.3 * pulse})`;
      g.lineWidth = 1;
      this.ellipse(x, ground, 17 * s, 7 * s);
      g.stroke();
    }
    if (e.mount === 'flying-sword' && !e.dead) this.drawFlyingSword(x, y, e, s);
    if ((e.mount === 'eagle' || e.mount === 'black-eagle') && !e.dead)
      this.drawEagle(x, y, e, s, e.mount === 'black-eagle' ? 'black' : 'brown');
    // The per-pair hit-stop (render/hitStop.ts) holds attacker and target on the contact frame.
    const shot = hitStopShot(e.id, this.world.time, () => this.frameOf(e, set, facing));
    // Swing smear: when a swing frame replaces another, leave the old one behind as a fading ghost.
    const A = this.effects.anim(e.id);
    if (e.swingT > 0 && set.poses.cut) {
      // A swing now sweeps through many frames: a ghost every few of them, not one per frame
      // (which blurs the figure into a double).
      const now = this.world.time;
      if (A.lastSwing && A.lastSwing !== shot.img && now - A.ghostAt > 0.09) {
        A.ghostAt = now;
        this.effects.fx.push({
          type: 'ghost',
          x: sx,
          y: sy - e.z - A.elev,
          t: 0,
          life: 0.14,
          img: A.lastSwing,
          flip: shot.facing < 0,
          ax: set.ax,
          ay: set.ay,
        });
      }
      A.lastSwing = shot.img;
    } else A.lastSwing = null;
    if (set.cloth) this.dressedSeen++;
    // In a crowd (a guild war) only the player's fabric streams: every figure's swinging cloth is
    // dozens of extra frames to draw and keep. Who else's streams follows the graphics tier (graphics.ts).
    const me = this.world.player;
    if (
      set.cloth &&
      !e.dead &&
      clothStreams(e === me, this.crowd >= CROWD, Math.hypot(e.x - me.x, e.y - me.y))
    ) {
      // Swap in the frame redrawn with the fabric still swinging.
      const h = this.iso.heading(e);
      const c = this.effects.clothOf(e, h === 2 || h === 6);
      shot.img = set.cloth(shot.img, c.sway, c.flutter);
    }
    x += Math.round(shot.dx);
    y += shot.dy;
    const img = shot.img;
    // Glows that cling to the figure step in its own grid's pixels (density.ts: half an art pixel
    // under option C). Rims are the exception: two G2 pixels (one G1) wide, as one read too thin,
    // filled at every G2 step so thin parts (a blade) get a solid rim, not a ghost beside them.
    const st = clingStep();
    const rimSteps = st < 1 ? [st, 1] : [1];
    const blit = (pic: HTMLCanvasElement, ox2 = 0, oy2 = 0) => {
      // Build the figure's affine transform directly; no native Canvas save/translate/restore
      // calls for every body and silhouette in a crowded WebGL scene.
      this.gpu.world.imageAt(
        pic,
        x + ox2,
        y + oy2 + shot.pivot * s,
        (artX(img) - set.ax) * s,
        (artY(img) - set.ay) * s - shot.pivot * s,
        artW(img) * s,
        artH(img) * s,
        shot.facing < 0 ? -1 : 1,
        shot.rot,
      );
    };
    // Charging glow: a coloured rim around the silhouette that pulses faster as release nears.
    const glow = e.cast
      ? e.cast.kind === 'break'
        ? '#ff9a30'
        : e.cast.kind === 'heal'
          ? '#ffe08a'
          : '#ff5a4a'
      : e.healT > 0
        ? '#ffe08a'
        : null;
    if (drawFigureRim(this.world, e, (c, dx, dy) => blit(silhouette(img, c), dx, dy))) void 0; // ตัวแดง / ตัวเหลือง (render/figureRims.ts)
    else if (glow && !e.dead) {
      const k = e.cast ? e.cast.t / e.cast.windup : 0.3;
      g.globalAlpha = a * (0.35 + 0.35 * Math.abs(Math.sin(this.world.time * (8 + k * 20))));
      const sil = silhouette(img, glow);
      for (const [gx, gy] of [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
      ] as const)
        for (const k of rimSteps) blit(sil, gx * k, gy * k);
      g.globalAlpha = a;
    }
    if (aura) {
      // ...and a steady rim of it around the figure, breathing slowly.
      const rim = silhouette(img, `rgb(${aura.color})`);
      const flick = Math.floor(this.world.time * 12) % 2;
      g.globalAlpha = a * (0.22 + 0.1 * Math.sin(this.world.time * 2.5));
      for (const [gx, gy] of [
        [-2, 0],
        [2, 0],
        [0, -3 - flick],
        [-1, -2],
        [1, -2],
      ] as const)
        blit(rim, gx, gy);
      g.globalAlpha = a * (0.4 + 0.15 * Math.sin(this.world.time * 2.5));
      for (const [gx, gy] of [
        [-1, 0],
        [1, 0],
        [0, -1],
      ] as const)
        for (const k of rimSteps) blit(rim, gx * k, gy * k);
      g.globalAlpha = a;
    }
    const since = this.world.time - A.auraAt;
    const auraLife = 0.35 + A.auraPow * 0.35;
    if (since < auraLife && !e.dead) {
      // Qi aura: the silhouette in the art's colour, spread around the body and licking upward
      // like flame, flickering as it fades.
      const f = 1 - since / auraLife;
      const sil = silhouette(img, `rgb(${A.auraCol})`);
      const flick = Math.floor(this.world.time * 24) % 2;
      const reach = A.auraPow >= 1 ? 2 : 1;
      g.globalAlpha = a * f * 0.55;
      for (let r = 1; r <= reach; r++)
        for (const [gx, gy] of [
          [-r, 0],
          [r, 0],
          [0, r],
          [0, -r - 1 - flick],
        ] as const)
          blit(sil, gx * st, gy * st);
      g.globalAlpha = a * f * 0.3;
      blit(sil, 0, -3 - flick * 2 - A.auraPow * 2);
      g.globalAlpha = a;
    }
    // วิ่งพันลี้: clones of the runner chase along the path just run, fading in a pale qi tint.
    if (e.sprinting && !e.dead) {
      A.trail.push({ x: sx, y: sy - e.z - A.elev, img, flip: shot.facing < 0, ax: set.ax, ay: set.ay });
      if (A.trail.length > 24) A.trail.shift();
      const n = A.trail.length;
      for (const [back, alpha] of [
        [18, 0.18],
        [12, 0.28],
        [6, 0.4],
      ] as const) {
        const c = A.trail[n - 1 - back];
        if (!c) continue;
        g.save();
        g.globalAlpha = a * alpha;
        g.translate(Math.round(c.x + ox), Math.round(c.y + oy));
        if (c.flip) g.scale(-1, 1);
        g.drawImage(
          silhouette(c.img, '#9fe0ff'),
          (artX(c.img) - c.ax) * s,
          (artY(c.img) - c.ay) * s,
          artW(c.img) * s,
          artH(c.img) * s,
        );
        g.globalAlpha = a * alpha * 0.8;
        g.drawImage(
          c.img,
          (artX(c.img) - c.ax) * s,
          (artY(c.img) - c.ay) * s,
          artW(c.img) * s,
          artH(c.img) * s,
        );
        g.restore();
      }
    } else if (A.trail.length) A.trail.length = 0;
    // Back skin: behind the body, or over it when the fighter turns their back to the camera.
    const backSkin = e.kind === 'player' && !e.dead ? this.world.progress.skinWorn.back : undefined;
    const backTurned = [5, 6, 7].includes(this.iso.heading(e));
    const greatSabre = !e.dead && carriesGreatSabre(e);
    if (greatSabre && !backTurned) this.drawGreatSabre(x, y, set.h * s, shot.facing, a);
    if (backSkin && !backTurned) this.drawBackSwords(x, y, e, set.h * s, shot.facing, a);
    // A forged weapon: its lightning and element, behind the figure and over it (render/forgeFx).
    const forged = !e.dead && forgeShows(e.forge) ? e.forge : null;
    const blades = forged ? bladesIn(shot.img) : [];
    const forgeFx = (layer: FxLayer) => {
      if (!forged || !blades.length) return;
      g.save();
      g.translate(x, y + shot.pivot * s);
      if (shot.facing < 0) g.scale(-1, 1);
      if (shot.rot) g.rotate(shot.rot);
      g.translate(-set.ax * s, -set.ay * s - shot.pivot * s);
      g.scale(s, s);
      // The glow underfoot stays on the ground through a jump; swings leave an afterimage.
      drawForgeFx(g, blades, forged, this.world.time, layer, e.id, {
        ground: { x: set.ax, y: set.ay + (ground - y) / s },
        trail: true,
      });
      g.restore();
    };
    forgeFx('back');
    blit(img);
    forgeFx('front');
    // A forging that just succeeded bursts over the player (render/forgeFx).
    if (e.id === this.world.player.id) drawForgeBurst(g, x, y - set.h * s * 0.45, s, this.world.time);
    drawAuras(g, auras, 'front', x - Math.round(shot.dx), y - shot.dy, s, this.world.time, a, e.id);
    if (greatSabre && backTurned) this.drawGreatSabre(x, y, set.h * s, shot.facing, a);
    if (backSkin && backTurned) this.drawBackSwords(x, y, e, set.h * s, shot.facing, a);
    if (since < 0.12 && A.auraPow >= 1 && !e.dead) {
      // The instant of release: the body itself flashes with qi.
      g.globalAlpha = a * (1 - since / 0.12) * 0.6;
      blit(silhouette(img, '#ffffff'));
      g.globalAlpha = a;
    }
    if (e.pk && !e.dead) {
      // ...and the body flushed blood-red, thirsting.
      g.globalAlpha = a * (0.26 + 0.12 * Math.sin(this.world.time * 4));
      blit(silhouette(img, '#ff1a28'));
      g.globalAlpha = a;
    }
    if (e.hitT > 0.05 && !e.dead && !e.blocking) {
      // White flash on the frame a blow lands.
      // In a bout only the first frame, and thin, so the figure stays readable.
      if (!this.world.pvpBout || e.hitT > 0.1) {
        g.globalAlpha = a * (this.world.pvpBout ? 0.35 : 0.75);
        blit(silhouette(img, '#ffffff'));
      }
      g.globalAlpha = a;
    }

    if (e.blocking && !this.world.pvpBout?.fight) {
      // Qi wall in front of the guard, with a glint running along its edge. Facing straight up or
      // down the screen it lies across the body instead of beside it.
      const ha = (this.iso.heading(e) * Math.PI) / 4;
      const across = Math.abs(Math.cos(ha)) < 0.5;
      const cx = x + Math.cos(ha) * 10 * s;
      const cy = y - set.h * 0.29 * s + Math.sin(ha) * 5 * s;
      const [rx, ry] = across ? [12 * s, 6 * s] : [5 * s, set.h * 0.27 * s];
      const f = across ? 1 : facing;
      const [a0, a1] = across ? [0, Math.PI * 2] : [-Math.PI / 2, Math.PI / 2];
      g.globalAlpha = a * (across && Math.sin(ha) < 0 ? 0.6 : 1);
      g.fillStyle = 'rgba(140,200,255,.16)';
      g.beginPath();
      g.ellipse(cx, cy, rx, ry, 0, a0, a1, f < 0);
      g.fill();
      g.strokeStyle = 'rgba(170,215,255,.75)';
      g.lineWidth = 1;
      g.beginPath();
      g.ellipse(cx, cy, rx, ry, 0, a0, a1, f < 0);
      g.stroke();
      const ga = a0 + ((this.world.time * 2.2) % 1) * (a1 - a0);
      g.fillStyle = '#ffffff';
      g.fillRect(Math.round(cx + Math.cos(ga) * rx * f), Math.round(cy + Math.sin(ga) * ry), 1, 2);
      g.globalAlpha = a;
    }
    // Frozen stiff (เหมันต์สะกดธาร): the ice it stands in, drawn with it so whoever is in front stays so.
    this.effects.drawHeld(g, e, x, y);
    if (e.stunT > 0 && !e.dead) {
      for (let k = 0; k < 3; k++) {
        const ang = e.animT * 6 + (k * Math.PI * 2) / 3;
        g.fillStyle = '#ffe36a';
        g.fillRect(
          Math.round(x + Math.cos(ang) * 7 * s),
          Math.round(y - set.h * s - 2 + Math.sin(ang) * 2),
          1,
          1,
        );
      }
    }
    g.globalAlpha = 1;
  }

  /**
   * The screen at the widest zoom (`minZoomPx()`), in world-picture pixels, centred on the picture
   * (its centre is the camera at every zoom).
   */
  private room(): { x: number; y: number; w: number; h: number } {
    const rpx = minZoomPx() * PIXEL_SIZE;
    const w = 2 * Math.ceil(this.w / rpx / 2);
    const h = 2 * Math.ceil(this.h / rpx / 2);
    return { x: (this.lowW - w) / 2, y: (this.lowH - h) / 2, w, h };
  }

  /**
   * The part of the world picture on screen at this zoom (it is sized for a whole step and
   * stretched by the fraction), in world-picture pixels.
   */
  private visible(): { x: number; y: number; w: number; h: number } {
    const w = this.w / this.px;
    const h = this.h / this.px;
    return { x: (this.lowW - w) / 2, y: (this.lowH - h) / 2, w, h };
  }

  /** A red paper lantern on a short pole in the hand away from the camera, glowing at night. */
  private drawLantern(e: Entity, ox: number, oy: number): void {
    const g = this.lg;
    const [sx, sy] = this.iso.proj(e.x, e.y);
    const f = this.iso.facing(e) || 1;
    const x = Math.round(sx + ox + f * 7);
    const sway = Math.round(Math.sin(this.world.time * 3 + e.id) * (e.moving ? 1.5 : 0.6));
    // Hung from the hand at the side, at the height of the figure's hand (render/lighting.ts).
    const y = Math.round(sy + oy - this.effects.stepElevation(e) - lanternLift(e, heightOf(this.sprites, e)));
    g.fillStyle = '#4a3020';
    g.fillRect(x - 1, y - 6, 1, 7);
    g.fillRect(x - 1, y - 6, 4, 1);
    const lx = x + 2 + sway;
    g.fillStyle = '#2a1a14';
    g.fillRect(lx - 2, y, 5, 1);
    g.fillRect(lx - 2, y + 8, 5, 1);
    g.fillStyle = '#d8382a';
    g.fillRect(lx - 3, y + 1, 7, 7);
    g.fillStyle = '#ffb05a';
    g.fillRect(lx - 1, y + 2, 3, 5);
    g.fillStyle = '#fff0b0';
    g.fillRect(lx, y + 3, 1, 3);
  }
}

const SABRE_LEFT = 25;
const SABRE_TOP = 8;
const SABRE_W = 53;
const SABRE_H = 15;
const SABRES: HTMLCanvasElement[] = [];
/**
 * The Blood Sabre school's great sabre for `drawGreatSabre`, facing right (`f` 1) or left (-1):
 * column `SABRE_LEFT` (mirrored for left) sits on the bearer's x, row `SABRE_TOP` on the sling line.
 */
function greatSabreImage(f: 1 | -1): HTMLCanvasElement {
  const cached = SABRES[f > 0 ? 0 : 1];
  if (cached) return cached;
  const c = canvas(SABRE_W, SABRE_H);
  const g = ctx2d(c);
  const ox = f > 0 ? SABRE_LEFT : SABRE_W - 1 - SABRE_LEFT;
  const X = (dx: number) => ox + dx * f;
  const by = SABRE_TOP;
  // Blade from the guard out past the far shoulder, its point sweeping up.
  for (let dx = -12; dx <= 26; dx++) {
    const curve = dx > 18 ? Math.round(((dx - 18) * (dx - 18)) / 14) : 0;
    const w = dx > 22 ? 3 : 5;
    const top = by - 2 - curve;
    g.fillStyle = '#4a4e58';
    g.fillRect(X(dx), top, 1, w);
    g.fillStyle = '#c8ccd4';
    g.fillRect(X(dx), top + 1, 1, w - 2);
    g.fillStyle = '#f2f4f8';
    g.fillRect(X(dx), top + w - 1, 1, 1);
    // Blood groove and stains.
    if (dx > -8 && dx < 20) {
      g.fillStyle = '#6a0a10';
      g.fillRect(X(dx), top + 1, 1, 1);
    }
    if ((dx * 7) % 9 < 3 && dx > -4) {
      g.fillStyle = '#9a0c16';
      g.fillRect(X(dx), top + 2, 1, 2);
    }
  }
  // Guard and red-wrapped grip (the ring pommel is stroked live).
  g.fillStyle = '#2a0a0e';
  g.fillRect(X(-13), by - 4, 2, 9);
  for (let dx = -22; dx <= -14; dx++) {
    g.fillStyle = dx % 2 ? '#8a1018' : '#c8202a';
    g.fillRect(X(dx), by - 1, 1, 3);
  }
  SABRES[f > 0 ? 0 : 1] = c;
  return c;
}
